import { ColorTheme, MealPlanFoodType } from '../Constants';
import { ExperimentNames } from '../experiments/ExperimentDefinitions';
import { ExceptFields, JSONObj, JSONType } from '../TypeUtilities';
import { AppLocationSource, AuthMethod, NavClickElementName } from './ClientTypes';
import { TrackableSharedEvents } from './SharedEvents';

export enum ReferralShareLocation {
  ACCOUNT_SHARE = 'account-share',
  POST_ORDER = 'post-order',
  ACCOUNT_SHARE_SHEF = 'account-share-shef',
  ACCOUNT_SHARE_FOOD_ITEM = 'account-share-food-item',
  ACCOUNT_SHARE_ORDER = 'account-share-order',
  AFTER_5_STAR_REVIEW = 'after-5-star-review',
  BECOME_A_SHEF = 'become-a-shef',
  WAITLIST = 'waitlist',
  UPCOMING_MEAL_PLAN_ORDERS = 'upcoming-meal-plan-orders',
  UPCOMING_ORDER_PURCHASE = 'upcoming-order-purchase',
  UPCOMING_ORDER_EDIT = 'upcoming-order-edit',
  UPCOMING_ORDER_AFTER_5_STAR_REVIEW = 'upcoming-order-after-5-star-review',
  EXPLORE_AFTER_5_STAR_REVIEW = 'explore-after-5-star-review',
  MANAGE_MEAL_PLAN = 'manage-meal-plan',
  EXPLORE_WSS_TAB = 'explore-wss-tab',
}

export enum ShareType {
  WHATSAPP = 'Whatsapp',
  TWITTER = 'Twitter',
  FACEBOOK = 'Facebook',
  EMAIL = 'Email',
  TEXT = 'Text',
  COPY = 'copy',
  NATIVE = 'native', // Keep 'native' lowercase due to legacy data.
}

export enum BrowseByTagsType {
  COLLECTION = 'collection',
  DIETARY_PREFERENCE = 'dietary-preference',
  CUISINE = 'cuisine',
}

export enum ShefBioPlacements {
  SHEF_PIC = 'shef_pic',
  SHEF_NAME = 'shef_name',
}

export type LoginGateType = 'page' | 'modal';

export enum LoginModalDismissElements {
  CLOSE_BUTTON = 'close',
  BROWSE_AS_GUEST = 'browse-as-guest',
}

export type MealPlanAnalyticsLocation =
  | 'all-shefs'
  | 'all-shefs-shef-menu'
  | 'cart'
  | 'dish-modal'
  | 'favorites-dish-modal'
  | 'favorites-shef-menu'
  | 'favorites-tab'
  | 'mains-tab'
  | 'sides-modal'
  | 'sides-page'
  | 'sides-tab'
  | 'sides-pairing'
  | 'sides-category'
  | 'all-sides'
  | 'your-shefs-shef-menu'
  | 'for-you'
  | 'add-ons-page'
  | 'wss-menu'
  | 'upcoming-order'
  | 'order-receipt'
  | 'orders-page'
  | 'marketplace-menu-preview'
  | 'homepage-sample-menu'
  | 'marketplace-wss-tab'
  | 'category-carousel'
  | 'search'
  | 'sides-search';

export type MealPlanUserType = 'single' | 'couple' | 'family' | 'other';

export type FollowButtonClickLocation = 'menu';

export type FoodItemRenderType = 'mains' | 'sides' | MealPlanFoodType;

export type FoodItemRenderPath = 'checkout' | 'edit';

export type DessertSectionArrivalMethod = 'banner' | 'pill' | 'scroll';

export type LeadCaptureStep = 'EMAIL' | 'PHONE';

/**
 * Shared by every reusable-packaging opt-in-modal event so a funnel from shown → how-it-works →
 * opt-in can be sliced on the same dimensions without joining across event shapes.
 */
export type ReusablePackagingOptInModalContext = {
  unit: number;
  numServings: number;
  regionId: number | undefined;
};

export enum ClientEvents {
  OTO_SHEF_MENU_SUBSCRIBE_SAVE_SHOWN = 'Oto-shef-menu-subscribe-save-shown',
  OTO_SHEF_MENU_SUBSCRIBE_SAVE_CLICKED = 'Oto-shef-menu-subscribe-save-clicked',
  // Unsaved Changes Modal events
  UNSAVED_CHANGES_MODAL_SHOWN = 'unsaved-changes-modal-shown',
  UNSAVED_CHANGES_MODAL_ACTION = 'unsaved-changes-modal-action',
  PURCHASE = 'purchase',
  EXISTING_USER_PURCHASE = 'existing-user-purchase',
  NEW_USER_PURCHASE = 'new-user-purchase',
  CART_ADD_ITEM = 'cart-add-item',
  CART_REMOVE_ITEM = 'cart-remove-item',
  FREE_DELIVERY_ADD_ITEM = 'free-delivery-add-item',
  PURCHASE_ATTEMPT = 'purchase-attempt',
  PURCHASE_ATTEMPT_FAILED = 'purchase-attempt-failed',
  SUBSCRIPTION_PURCHASE_ATTEMPT = 'subscription-purchase-attempt',
  SUBSCRIPTION_PURCHASE = 'subscription-purchase',

  // Meal Planning MVP
  MEAL_PLANNING_MVP_FOOD_ITEM_RENDERED = 'meal-planning-mvp-food-item-rendered',
  MEAL_PLANNING_MVP_CAROUSEL_TAB = 'meal-planning-carousel-food-item-type-tab',
  MEAL_PLANNING_MVP_ADD_TO_CART = 'meal_planning_mvp_add_to_cart',
  MEAL_PLANNING_MVP_REMOVE_FROM_CART = 'meal_planning_mvp_remove_from_cart',
  MEAL_PLANNING_MVP_SIDE_OR_STAPLE_ADD = 'meal_planning_mvp_side_or_staple_add',
  MEAL_PLANNING_MVP_ADD_ON_ADD = 'meal_planning_mvp_add_on_add',
  CATEGORY_CAROUSEL_CLICK = 'category_carousel_click',
  CATEGORY_CAROUSEL_CLEAR = 'category_carousel_clear',
  MEAL_PLAN_CATEGORY_SORT_APPLIED = 'meal-plan-category-sort-applied',
  COLLECTION_RENDERED = 'collection_rendered',
  MEAL_PLANNING_MVP_CHECKOUT_STARTED = 'meal-planning-mvp-checkout-started',
  MEAL_PLANNING_MVP_CHECKOUT_VALIDATION_FAILURE = 'meal-planning-mvp-checkout-validation-failure',
  MEAL_PLANNING_MVP_CHECKOUT_VALIDATION_FAILURE_TREATMENT = 'meal-planning-mvp-checkout-validation-failure-treatment',
  MEAL_PLANNING_MVP_DELIVERY_ADDRESS_SELECT = 'meal-planning-mvp-checkout-delivery-address-select',
  MEAL_PLANNING_MVP_DELIVERY_TIME_SELECT = 'meal-planning-mvp-checkout-delivery-time-select',
  MEAL_PLANNING_MVP_CHECKOUT_INPUT_STARTED = 'meal-planning-mvp-checkout-input-started',
  MEAL_PLANNING_MVP_PURCHASE = 'meal-planning-mvp-purchase',
  MEAL_PLANNING_MVP_PURCHASE_ATTEMPT = 'meal-planning-mvp-purchase-attempt',
  EXPRESS_CHECKOUT_ATTEMPT = 'express-checkout-attempt',
  EXPRESS_CHECKOUT_V1_WALLET_AVAILABILITY = 'express-checkout-v1-wallet-availability',
  EXPRESS_CHECKOUT_V1_FAILURE = 'express-checkout-v1-failure',
  INLINE_LINK_BUTTON_CLICKED = 'inline-link-button-clicked',
  INLINE_LINK_CARD_CONFIRMED = 'inline-link-card-confirmed',
  INLINE_LINK_FLOW_EXITED = 'inline-link-flow-exited',
  MEAL_PLANNING_MVP_MORE_SHEFS_CLICK = 'meal-planning-mvp-more-shefs-click',
  MEAL_PLANNING_MVP_DISH_UPDATE_ATTEMPT = 'meal-planning-mvp-dish-update-attempt',
  MEAL_PLANNING_MVP_SKIP_DELIVERY_ATTEMPT = 'meal-planning-mvp-skip-delivery-attempt',
  MEAL_PLANNING_SERVING_SIZE_MODAL_SHOWN = 'meal-planning-serving-size-modal-shown',
  MEAL_PLANNING_SERVING_SIZE_INFO_MODAL_OPEN = 'meal-planning-serving-size-info-modal-open',
  MEAL_PLANNING_SPLASH_PAGE_LOADED = 'meal-planning-splash-page-loaded',
  MEAL_PLANNING_UPCOMING_ORDER_RENDERED = 'meal-planning-upcoming-order-rendered',
  MEAL_PLANNING_UNSKIP_DELIVERY = 'meal-planning-unskip-delivery',
  MEAL_PLANNING_UNSKIP_DELIVERY_ERROR = 'meal-planning-unskip-delivery-error',
  MEAL_PLANNING_UPCOMING_ORDER_PAGE_VIEW = 'meal-planning-upcoming-order-page-loaded',
  MEAL_PLANNING_UPCOMING_ORDER_BTN_CLICK = 'meal-planning-upcoming-order-btn-click',
  MEAL_PLANNING_EDIT_DELIVERY_DATE_BTN_CLICK = 'meal-planning-edit-delivery-date-btn-click',
  MEAL_PLANNING_MVP_IMPERFECT_MATCH_RESULTS = 'meal-planning-mvp-imperfect-match-results',
  MEAL_PLANNING_PREFERENCES_SUBMITTED = 'meal-planning-preferences-submitted',
  MEAL_PLAN_SHEF_BIO_CLICK = 'meal-plan-shef-bio-click',
  MANAGE_SUBSCRIPTION_DROPDOWN = 'manage-subscription-dropdown',
  SUBSCRIPTION_CANCEL_MODAL_CONFIRMATION = 'subscription-cancel-modal-confirmation',
  MEAL_PLAN_OPEN_CART = 'meal_plan_open_cart',
  EXPLORE_PERSONAL_MEAL_PLAN_BANNER_SHOWN = 'explore-personal-meal-plan-banner-shown',
  EXPLORE_PERSONAL_MEAL_PLAN_BANNER_CLICKED = 'explore-personal-meal_plan-banner-clicked',
  EXPLORE_MEAL_PLAN_MODAL_SHOWN = 'explore-meal-plan-modal-shown',
  EXPLORE_MEAL_PLAN_MODAL_CLAIM_OFFER = 'explore-meal-plan-modal-claim-offer',
  EXPLORE_MEAL_PLAN_MODAL_CLOSED = 'explore-meal-plan-modal-closed',
  BACK_TO_TOP_BUTTON_CLICKED = 'back-to-top-button-clicked',
  MEAL_PLAN_MERCH_BAR_CLICK = 'meal-plan-merch-bar-click',
  MEAL_PLAN_SEARCH_RESULTS_DISPLAYED = 'meal-plan-search-results-displayed',
  MEAL_PLAN_SEARCH_RESULT_CLICK = 'meal-plan-search-result-click',
  MEAL_PLAN_SEARCH_ICON_CLICK = 'meal-plan-search-icon-click',

  // Flexible Sides Events
  MEAL_PLAN_SIDE_MODAL_SHOWN = 'meal-plan-side-modal-shown',
  MEAL_PLAN_SIDE_MODAL_SHEF_RENDERED = 'meal-plan-side-modal-shef-rendered',
  MEAL_PLAN_REVIEW_ORDER_SHOWN = 'meal-plan-review-order-shown',
  MEAL_PLAN_REVIEW_ORDER_CLOSED = 'meal-plan-review-order-closed',
  MEAL_PLAN_REVIEW_ORDER_EDIT_CLICK = 'meal-plan-review-order-edit-click',
  MEAL_PLAN_REVIEW_ORDER_CHECKOUT_CLICK = 'meal-plan-review-order-checkout-click',
  MEAL_PLAN_DISH_TYPE_NAVIGATION = 'meal-plan-dish-type-navigation',
  MEAL_PLAN_SIDE_MODAL_DISMISSED = 'meal-plan-side-modal-dismissed',
  MEAL_PLAN_SIDE_MODAL_DONT_SHOW_AGAIN = 'meal-plan-side-modal-dont-show-again',
  MEAL_PLAN_FLEX_SERVINGS_UPSELL_SHOWN = 'meal-plan-flex-servings-upsell-shown',
  MEAL_PLAN_MAINS_CART_FILLED = 'meal-plan-mains-cart-filled',
  MEAL_PLAN_ADD_ONS_CATEGORY_SELECTED = 'meal_plan_add_ons_category_selected',
  MEAL_PLAN_DESSERT_SECTION_VIEWED = 'meal_plan_dessert_section_viewed',
  MEAL_PLAN_DESSERT_BANNER_SHOWN = 'meal_plan_dessert_banner_shown',
  MEAL_PLAN_DESSERT_BANNER_CLICK = 'meal_plan_dessert_banner_click',
  MEAL_PLAN_CLEAR_CART = 'meal-plan-clear-cart',
  MEAL_PLAN_DELIVERY_DAY_SHOWN = 'meal-plan-delivery-day-shown',
  MEAL_PLAN_BROWSE_EXPERIENCE_CLICK = 'meal-plan-browse-experience-click',
  MEAL_PLAN_DISH_SELECTION_BACK_CLICK = 'meal-plan-dish-selection-back-click',
  MEAL_PLAN_SIDES_CATEGORY_SELECTED = 'meal-plan-sides-category-selected',
  MEAL_SELECTION_FOOTER_NEXT_CLICK = 'meal-selection-footer-next-click',
  MEAL_PLAN_CHECKOUT_NEXT_STEP = 'meal-plan-checkout-next-step',
  MEAL_PLAN_CHECKOUT_PREV_STEP = 'meal-plan-checkout-prev-step',
  MEAL_PLAN_CHECKOUT_STEP_ERROR = 'meal-plan-checkout-step-error',
  MEAL_PLAN_INTAKE_NEXT_STEP = 'meal-plan-intake-next-step',
  MEAL_PLAN_INTAKE_STEP_ERROR = 'meal-plan-intake-step-error',
  MEAL_PLAN_INTAKE_COMPLETE_PREFERENCE = 'meal-plan-intake-complete-preference',
  MEAL_PLAN_INTAKE_REGION_INVALIDATION = 'meal-plan-intake-region-invalidation',
  MEAL_PLAN_INTAKE_REDIRECT_TO_CHECKOUT = 'meal-plan-intake-redirect-to-checkout',
  MEAL_PLAN_INTAKE_LAST_STEP = 'meal-plan-intake-last-step',
  STEPPER_NEXT_STEP_CLICKED = 'stepper-next-step-clicked',
  STEPPER_STEP_ERROR = 'stepper-step-error',
  STEPPER_NAVIGATION_BLOCKED = 'stepper-navigation-blocked',
  STEPPER_NEXT_CALLBACK_ERROR = 'stepper-next-callback-error',
  MEAL_PLAN_SIDES_PAIRING_EMPTY_CAROUSEL = 'meal-plan-sides-pairing-empty-carousel',
  MEAL_PLAN_SIDES_PAIRING_BROWSE_ALL_SIDES_CLICK = 'meal-plan-sides-pairing-browse-all-sides-click',
  MEAL_PLAN_SIDES_PAIRING_EXPLORE_MORE_CLICK = 'meal-plan-sides-pairing-explore-more-click',

  // Complete Meal Events
  INTAKE_SPLASH_SCREEN = 'intake-splash-screen',
  INTAKE_HOUSEHOLD = 'intake-household',
  INTAKE_INGREDIENTS_TO_AVOID = 'intake-ingredients-to-avoid',
  INTAKE_INGREDIENTS_TO_AVOID_OTHER = 'intake-ingredients-to-avoid-other',
  INTAKE_PORTION_SIZE_SELECTED = 'intake-portion-size-selected',
  INTAKE_ORGANIC_AND_MORE = 'intake-organic-and-more',
  INTAKE_SERVINGS = 'intake-servings',
  INTAKE_SETTINGS = 'intake-settings',
  INTAKE_DIET = 'intake-diet',
  INTAKE_DELIVERY_DAY = 'intake-delivery-day',
  // Intake Optimization (INTAKE_OPTIMIZATION experiment)
  INTAKE_PROTEIN = 'intake-protein',
  INTAKE_CUISINES = 'intake-cuisines',
  INTAKE_HOUSEHOLD_PREFERENCES = 'intake-household-preferences',
  INTAKE_LOADING_VIEWED = 'intake-loading-viewed',
  INTAKE_SELECTION_DESYNC = 'intake-selection-desync',
  SAMPLE_MENU_VIEWED = 'sample-menu-viewed',
  SKIP_ALL_SELECTED = 'skip-all-selected',
  MEAL_PLAN_FILTERS_APPLIED = 'meal-plan-filters-applied',
  MEAL_PLAN_FILTERS_OPENED = 'meal-plan-filters-opened',
  MEAL_PLAN_EDIT_TASTE_PROFILE_CLICKED = 'meal-plan-edit-taste-profile-clicked',
  MEAL_PLAN_TASTE_PROFILE_EDITED = 'meal-plan-taste-profile-edited',
  MEAL_PLAN_TASTE_PROFILE_INTAKE = 'meal-plan-taste-profile-intake',
  MEAL_PLAN_REVIEW_ORDER_SUMMARY_CLICK = 'meal-plan-review-order-summary-click',
  MEAL_PLAN_ORDER_DETAILS_CLICK = 'meal-plan-order-details-click',
  CHECKOUT_TIMER_ELAPSED = 'checkout-timer-elapsed',
  PAYMENT_DETAILS_INPUT_STARTED = 'payment-details-input-started',

  // Homepage Events
  HOMEPAGE_FAQ_CLICK = 'homepage-faq-click',
  HOMEPAGE_MEAL_PLAN_CLICK = 'homepage-meal-plan-click',
  HOMEPAGE_MARKETPLACE_CLICK = 'homepage-marketplace-click',
  HOMEPAGE_CATEGORY_CAROUSEL_CLICK = 'homepage-category-carousel-click',
  HOMEPAGE_DISH_STORY_VIEW = 'homepage-dish-story-view',
  HOMEPAGE_REDIRECT = 'homepage-redirect',
  HOMEPAGE_VIEW = 'homepage-view',

  // Modifying Meal Plan Events
  MEAL_PLANNING_MANAGE_MEAL_PLAN_BTN_CLICK = 'meal-planning-manage-meal-plan-btn-click',
  MEAL_PLAN_MODIFIED_CLICKED = 'meal-plan-modified-clicked',
  MEAL_PLANNING_MODIFY_PLAN_FAILURE = 'meal-planning-modify-plan-failure',
  MEAL_PLANNING_PLAN_SETTINGS_INPUT_CLICK = 'meal-planning-plan-settings-input-click',
  MEAL_PLANNING_DELIVERY_DAY_INPUT_CLICK = 'meal-planning-delivery-day-input-click',
  MEAL_PLAN_RESUME_CLICKED = 'meal-plan-resume-clicked',
  MEAL_PLAN_PAUSE_OR_CANCEL_CLICKED = 'meal-plan-pause-or-cancel-clicked',
  PICK_UP_WHERE_YOU_LEFT_MODAL_SHOWN = 'pick-up-where-you-left-modal-shown',
  PICK_UP_WHERE_YOU_LEFT_MODAL_ACTION = 'pick-up-where-you-left-modal-action',
  DISH_SELECTION_CART_VALIDATION = 'dish-selection-cart-validation',
  NEW_USER_INTAKE_CHANGES = 'new-user-intake-changes',

  // Weekly Shef Service / Marketplace Events
  WSS_GET_STARTED_CLICK = 'wss-get-started-click',
  WSS_EXPLORE_TAB_CLICK = 'wss-explore-tab-click',
  WSS_TAB_VIEW_UPCOMING = 'wss-tab-view-upcoming',
  WSS_ORGANIC_INFO_MODAL_SHOWN = 'wss-organic-info-modal-shown',
  WSS_ORGANIC_INFO_MODAL_DISMISSED = 'wss-organic-info-modal-dismissed',
  WSS_FLEX_SERVINGS_MINIMUM_MET_TOAST_SHOWN = 'wss-flex-servings-minimum-met-toast-shown',

  // TODO Trevomatic Remove after we figure out the zipCode / purchase failure issue
  MEAL_PLAN_NOT_AVAILABLE_ERROR = 'meal-plan-not-available-error',

  // TODO Trevomatic Remove this after we validate the new mealPlanForUser store
  MEAL_PLAN_FOR_USER_VALIDATION_FAILURE = 'meal-plan-for-user-validation-failure',
  TASTE_PROFILE_STORE_VALIDATION_FAILURE = 'taste-profile-store-validation-failure',

  MEAL_PLANNING_MVP_INTAKE_FORM_STEP = 'meal-planning-mvp-intake-form-step',

  EXPLORE_MEAL_PLAN_HERO_BANNER_CLICK = 'explore-meal-plan-hero-banner-click',
  EXPLORE_MEAL_PLAN_HERO_BANNER_SHOWN = 'explore-meal-plan-hero-banner-shown',
  EXPLORE_MEAL_PLAN_HERO_BANNER_EXISTING_SUB_CLICK = 'explore-meal-plan-hero-banner-existing-sub-click',
  EXPLORE_MEAL_PLAN_HERO_BANNER_EXISTING_SUB_SHOWN = 'explore-meal-plan-hero-banner-existing-sub-shown',

  ORGANIZATION_LANDING = 'organization-landing',
  SHEF_APPLICATION_RECEIVED = 'shef-application-received',
  SHEF_MEAL_PLAN_APPLICATION_STARTED = 'shef-meal-plan-application-started',
  SHEF_MEAL_PLAN_QUESTIONNAIRE_COMPLETED = 'shef-meal-plan-questionnaire-completed',
  SHEF_MEAL_PLAN_APPLICATION_COMPLETED = 'shef-meal-plan-application-completed',
  SHEF_ONBOARDING_NON_HUB_OPTION_SELECT = 'shef-onboarding-non-hub-option-select',

  SUBSCRIPTION_MEAL_DETAILS_CLICK = 'subscription-meal-details-click',

  SHARED_NONE = 'share-none', // Should not ever be shared
  SHARE_SHEF_MENU = 'share-shef-menu',
  SHARE_SHEF_FOOD_ITEM = 'share-shef-food-item',
  SHARE_SHEF_PROMO_CODE = 'shate-shef-promo-code',
  REFERRAL_CODE_COPIED = 'referral-code-copied',
  REFERRAL_CODE_SHARED = 'referral-code-shared',
  REFERRAL_CODE_SHARED_FACEBOOK = 'referral-code-shared-facebook',
  REFERRAL_CODE_SHARED_TWITTER = 'referral-code-shared-twitter',
  REFERRAL_CODE_SHARED_WHATSAPP = 'referral-code-shared-whatsapp',
  REFERRAL_CODE_SHARED_EMAIL = 'referral-code-shared-email',
  REFERRAL_MEAL_PLAN_MODAL_VIEW = 'referral-meal-plan-modal-view',
  REFERRAL_MEAL_PLAN_MODAL_CLOSE = 'referral-meal-plan-modal-close',
  REFERRAL_MEAL_PLAN_MODAL_LEARN_CLICK = 'referral-meal-plan-modal-learn-click',
  REFER_COMPOSE_EMAIL = 'refer-compose-email',

  FOOD_SEARCH = 'food-search',
  FOOD_SEARCH_RESULTS_DISPLAYED = 'food-search-results-displayed',
  SHEF_SEARCH_RESULT_CLICK = 'shef-search-result-click',
  FOOD_SEARCH_RESULT_CLICK = 'food-search-result-click',
  CUISINE_SEARCH_RESULT_CLICK = 'cuisine-search-result-click',
  SEARCH_SHOW_MORE_SHEFS_CLICK = 'search-show-more-shefs-click',
  SEARCH_SHOW_MORE_DISHES_CLICK = 'search-show-more-dishes-click',
  MORE_CUISINES_CLICK = 'more-cuisines-click',
  GIFT_CARD_SEARCH_RESULT_CLICK = 'gift-card-search-result-click',
  TRENDING_SEARCH_CLICK = 'trending-search',

  API_SYNC_RELOAD = 'api-sync-reload',
  SESSION_STARTED = 'session-started',

  BECOME_A_SHEF_VISIT = 'become-a-shef-visit',

  PAGE_VIEW = 'page-view',
  PAGE_NOT_FOUND = 'page-not-found',

  SUBMIT_DELIVERY_DAY_FILTER = 'submit-delivery-day-filter',
  CLICK_DIETARY_FILTER = 'click-dietary-filter',
  SUBMIT_DIETARY_FILTER = 'submit-dietary-filter',
  CLICK_SPICE_FILTER = 'click-spice-filter',
  SUBMIT_SPICE_FILTER = 'submit-spice-filter',
  SUBMIT_CUISINE_CATEGORY_FILTER = 'submit-cuisine-category-filter',
  SELECT_CUISINE_FILTER = 'select-cuisine-filter',
  CLICK_PICKUP_DISTANCE_FILTER = 'click-pickup-distance-filter',
  // SUBMIT_PICKUP_DISTANCE_FILTER = 'submit-pickup-distance-filter', // deprecated 11-29-2022
  CAROUSEL_SWIPE = 'carousel-swipe',
  LANDING_ZIP_SUBMIT = 'landing-zip-submit',
  EXPLORE_ZIP_SUBMIT = 'explore-zip-submit',
  VIDEO_BIO_CLICK = 'video-bio-click',
  LEARN_MORE_CLICK = 'learn-more-click',
  CART_INCREMENT = 'cart-increment',
  CART_DECREMENT = 'cart-decrement',
  EXPLORE_SEGMENT_C2C_REFER_CLICK = 'explore-segment-c2c-refer-click',
  SHEF_SPOTLIGHT_CLICK = 'shef-spotlight-click',
  RECENTLY_REVIEWED_CLICK = 'recently-reviewed-click',

  // 4/8/22 Deprecated. Remove after no events have been logged for a while.
  NAV_HAMBUGER_MENU_CLICK = 'nav-hamburger-menu-click',

  NAV_CLICK = 'nav-click',

  AUTO_APPLIED_REWARD_PROMO = 'auto-applied-reward-promo',

  SHEF_MENU_VISIT = 'shef-menu-visit',
  DISH_MODAL_VISIT = 'dish-modal-visit',
  DISH_MODAL_VARIANT_DECISION = 'dish-modal-variant-decision',
  SHEF_PROFILE_IMAGE_CLICK = 'shef-profile-image-click',
  DISH_IMAGE_CLICK = 'dish-image-click',
  PREFAB_FOOD_ITEM_COPY = 'prefab-food-item-copy',
  FOOD_ITEM_SUGGESTIONS_VIEW = 'food-item-suggestions-view',

  AUTO_APPLIED_PROMO = 'auto-applied-promo',
  AUTO_APPLIED_SUBSCRIPTION_PROMO = 'auto-applied-subscription-promo',

  BECOME_A_SHEF_CLICK_GET_STARTED = 'become-a-shef-click-get-started',
  BECOME_A_SHEF_ENTER_INFO = 'become-a-shef-enter-info',
  BECOME_A_SHEF_CLICK_SIGN_UP = 'become-a-shef-click-sign-up',

  LOGIN_MODAL_EMAIL_LOGIN_SUBMIT = 'login-modal-email-login-submit',
  LOGIN_FAILED = 'login-failed',
  LOGIN_MODAL_SIGNUP_SUBMIT = 'login-modal-signup-submit',
  SIGN_UP_ERROR = 'sign-up-error',
  SSO_AUTH_SUCCESS = 'sso-auth-success',
  SSO_AUTH_FAILED = 'sso-auth-failed',
  REGISTER_SUCCESS = 'register-success',
  LOGIN_MODAL_OPENED = 'login-modal-opened',
  LOGIN_MODAL_DISMISSED = 'login-modal-dismissed',
  LOGIN_MODAL_SIGNUP_PAGE = 'login-modal-signup-page',
  LOGIN_MODAL_EMAIL_LOGIN_PAGE = 'login-modal-email-login-page',
  LOGIN_MODAL_EMAIL_ENTERED = 'login-modal-email-entered',
  LOGIN_MODAL_FORGOT_PASSWORD_CLICKED = 'login-modal-forgot-password-clicked',
  LOGIN_PAGE_VIEWED = 'login-page-viewed',

  LEAD_CAPTURE_FIELD_SUBMITTED = 'lead-capture-field-submitted',
  LEAD_CAPTURE_ERROR = 'lead-capture-error',
  LEAD_CAPTURE_MODAL_DISMISSED = 'lead-capture-modal-dismissed',
  LEAD_CAPTURE_MODAL_VIEWED = 'lead-capture-modal-viewed',
  LEAD_CAPTURE_AUTOFILL_APPLIED = 'lead-capture-autofill-applied',
  SSO_SIGNUP_CLICKED = 'sso-signup-clicked',
  GOOGLE_ONE_TAP_PROMPT_MOMENT = 'google-one-tap-prompt-moment',
  GOOGLE_ONE_TAP_CREDENTIAL_RETURNED = 'google-one-tap-credential-returned',

  CHECKOUT_DELIVERY_ADDRESS_SELECT = 'checkout-delivery-address-select',
  CHECKOUT_DELIVERY_TIME_SELECT = 'checkout-delivery-time-select',
  CHECKOUT_CREDIT_CARD_INPUT = 'checkout-credit-card-input',
  CHECKOUT_TIP_VALUE_UPDATE = 'checkout-tip-value-update',
  CHECKOUT_ORDER_ERROR = 'checkout-order-error',
  CHECKOUT_LEGAL_PROMPT = 'checkout-legal-prompt',

  NEW_USER_EDUCATION_VIEW = 'new-user-education-view',
  NEW_USER_EDUCATION_STEP = 'new-user-education-step',

  CONSUMER_TRIAL_EXPLANATION_VIEW = 'consumer-trial-explanation-view',
  CONSUMER_TRIAL_ONBOARDING_VIEW = 'consumer-trial-onboarding-view',
  CONSUMER_TRIAL_ONBOARDING_ADVANCE = 'consumer-trial-onboarding-advance',
  CONSUMER_TRIAL_ONBOARDING_ACCEPT = 'consumer-trial-onboarding-accept',

  CONSUMER_HEADER_PROMO_BANNER_CLICK = 'consumer-header-promo-banner-click',
  CONSUMER_HEADER_PERSISTENT_PROMO_BANNER_DISMISSED = 'consumer-header-persistent-promo-banner-dismissed',

  COCKTAIL_ID_REMINDER_ACTION = 'cocktail-id-reminder-action', // deprecated 2022-03-11
  COCKTAIL_PURCHASE_ATTEMPT = 'cocktail-purchase-attempt', // deprecated 2022-03-11

  SHEF_FRINGE_MODAL_EXPLANATION_VIEW = 'shef-fringe-modal-explanation-view',
  SHEF_FRINGE_MODAL_MORE_VIEWED = 'shef-fringe-modal-more-viewed',
  SHEF_FRINGE_MODAL_DISMISSED = 'shef-fringe-modal-dismissed',
  SHEF_FRINGE_MODAL_ACCEPTED = 'shef-fringe-modal-accepted',
  SHEF_APPLICATION_TRACKER_SHOW = 'shef-application-tracker-show',
  SHEF_APPLICATION_TRACKER_HIDE = 'shef-application-tracker-hide',
  SHEF_APPLICATION_TRACKER_CLICK_STEP = 'shef-application-tracker-click-step',

  UPSELL_CLICK_ADD_TO_CART = 'upsell-click-add-to-cart', // deprecated 2023-04-19
  UPSELL_CLICK_CONTINUE = 'upsell-click-continue', // deprecated 2023-04-19
  UPSELL_CLICK_NO_THANKS = 'upsell-click-no-thanks', // deprecated 2023-04-19

  ATTEMPTED_REDIRECT_TO_CHROME_IOS = 'attempted-redirect-to-chrome-ios',

  ADD_TO_CALENDAR_CLICK = 'add-to-calendar-click',

  FOLLOW_SHEF_START = 'follow-shef-start',
  FOLLOW_SHEF_STOP = 'follow-shef-stop',
  FOOD_ITEM_FAVORITE_ADDED = 'food-item-favorite-added',
  FOOD_ITEM_FAVORITE_REMOVED = 'food-item-favorite-removed',
  SHEF_FAVORITE_ADDED = 'shef-favorite-added',
  SHEF_FAVORITE_REMOVED = 'shef-favorite-removed',
  FAVORITES_INFO_MODAL_SHOWN = 'favorites-info-modal-shown',
  FAVORITES_INFO_MODAL_DISMISSED = 'favorites-info-modal-dismissed',
  SHEF_POST_VIEW = 'shef-post-view',
  VIEW_MENU_REVIEWS_SECTION = 'view-menu-reviews-section',
  CLICK_REVIEWS_BUTTON = 'click-reviews-button',
  VIEW_FOOD_SAFETY_POPOVER = 'view-food-safety-popover',
  REVIEW_FOOD_ITEM_IMAGE_CLICK = 'review-food-item-image-click',
  APP_REDIRECT = 'app-redirect',
  NAVIGATE_TO_APP_LISTING = 'navigate-to-app-listing',
  APP_UPSELL_BANNER_DISMISSED = 'app-upsell-banner-dismissed',
  WEDGE_PAGE_VIEW = 'wedge-page-view',
  DELIVERY_DATE_TOOLTIP_OPEN = 'delivery-date-tooltip-open',
  CHAT_WIDGET_OPEN = 'chat-widget-open',
  SHEF_MESSAGE_CLICK = 'meal-plan-shef-message-click',
  FLAGGED_CHAT_MODAL_EVENT = 'flagged-chat-modal-event',

  SHEF_ROSTER_SELECTOR_CLICK = 'shef-selector-click',
  SHEF_ROSTER_LOADED = 'your-shefs-returned',
  SHEF_MENU_CLICK = 'shef-menu-click',

  SHEF_ROSTER_ADD_FAVORITES = 'shef-roster-add-favorites',
  SHEF_ROSTER_ADD_FAVORITES_BROWSE_ALL = 'shef-roster-add-favorites-browse-all',
  SHEF_PROFILE_LOAD_MORE_REVIEWS = 'shef-menu-load-more-reviews',

  SINGLE_CART_STARTED_CHECKOUT = 'single-cart-started-checkout',
  MULTI_CART_STARTED_CHECKOUT = 'multi-cart-started-checkout',
  MULTI_CART_ADD_ORDER_BTN_CLICKED = 'multi-cart-add-order-button-clicked', // deprecated 2022-08022
  MULTI_CART_STARTED_NEW_ORDER = 'multi-cart-started-new-order',
  MULTI_CART_DELIVERY_DATE_CONFLICT_SHOWN = 'multi-cart-delivery-date-conflict-shown',
  MULTI_CART_DELIVERY_DATE_CONFLICT_RESOLVED = 'multi-cart-delivery-date-conflict-resolved',
  DISH_FIRST_CART_CHANGE_DELIVERY_DATE = 'dish-first-cart-change-delivery-date',
  DISH_FIRST_CLICK_NEXT_DAY_BANNER = 'dish-first-click-next-day-banner',
  DISH_FIRST_LAZY_LOAD_QUICK_ADD_ITEM = 'dish-first-lazy-load-quick-add-item',

  REORDER_BUTTON_CLICK = 'reorder-button-click',
  RECURRING_SUBSCRIPTION_OPTION_SELECT = 'recurring-subscription-option-select',
  RECURRING_SUBSCRIPTION_CANCEL_BUTTON_CLICK = 'recurring-subscription-cancel-button-click',
  RECURRING_SUBSCRIPTION_SKIP_ORDER_BUTTON_CLICK = 'recurring-subscription-skip-order-button-click',

  SUBMIT_DISH_FILTER = 'submit-dish-filter',

  // Browse By Tags
  BROWSE_BY_TAGS_CLICK = 'browse-by-tags-click',

  DELIVERY_DAY_FILTER_ARROW_CLICK = 'delivery-day-filter-arrow-click',
  SEGMENT_ARROW_CLICK = 'segment-arrow-click',
  SEGMENT_SEE_ALL_LINK_CLICK = 'segment-see-all-link-click',
  SEGMENT_ITEM_CLICK = 'clicked-segment-item',

  BROWSE_BY_MEAL_TYPE_CLICK = 'browse-by-meal-type-click',
  BROWSE_BY_SHEF_CLICK = 'browse-by-shef-click',
  CELEBRITY_EVENT_EXPLORE_CLICK = 'celebrity-event-explore-click',
  INDIAN_MARKET_EXPLORE_CLICK = 'indian-market-explore-click',

  BULK_DISCOUNT_EXPLORE_CLICK = 'bulk-discount-explore-click',
  BULK_DISCOUNT_MODAL_VIEW = 'bulk-discount-modal-view',
  BULK_DISCOUNT_MODAL_CLOSE = 'bulk-discount-modal-close',

  // Dish details modal
  DISH_DETAILS_TOGGLE_DETAILS = 'dish-details-toggle-details',
  DISH_DETAILS_SHEF_MENU_SEE_ALL_CLICK = 'dish-details-shef-menu-see-all-click',

  EXPLORE_SEE_MORE_CLICK = 'explore-see-more-click',
  EXPLORE_FOOD_ITEMS_RENDERED = 'explore-food-items-rendered',

  DEVICE_ZIP_TIME_ZONE_DIFFERENCE = 'device-zip-time-zone-difference',

  // Reusable Packaging
  REUSABLE_PACKAGING_OPT_IN_SELECTION = 'reusable-packaging-opt-in-selection',
  INTAKE_REUSABLE_HOW_IT_WORKS_CLICK = 'intake-reusable-how-it-works-click',
  PACKAGING_TOGGLE_CHANGED = 'packaging_toggle_changed',
  REUSABLE_PACKAGING_OPT_IN_MODAL_SHOWN = 'reusable-packaging-opt-in-modal-shown',
  REUSABLE_PACKAGING_OPT_IN_MODAL_HOW_IT_WORKS_CLICK = 'reusable-packaging-opt-in-modal-how-it-works-click',
  REUSABLE_PACKAGING_OPT_IN_MODAL_OPT_IN_CLICK = 'reusable-packaging-opt-in-modal-opt-in-click',
  REUSABLE_PACKAGING_OPT_IN_MODAL_DISMISSED = 'reusable-packaging-opt-in-modal-dismissed',

  // Shef Dashboard Analytics
  SHEF_DASHBOARD_MY_PROFILE_CLICK = 'shef-dashboard-my-profile-click',
  SHEF_DASHBOARD_MY_MENU_TAB_CLICK = 'shef-dashboard-my-menu-tab-click',
  SHEF_DASHBOARD_ADD_NEW_DISH_BTN_CLICK = 'shef-dashboard-add-new-dish-btn-click',
  SHEF_DASHBOARD_CREATE_NEW_FOOD_ITEM = 'shef-dashboard-create-new-food-item',
  SHEF_DASHBOARD_FOOD_ITEM_CHANGE_SAVE = 'shef-dashboard-food-item-change-save',
  SHEF_DASHBOARD_MY_RATINGS_TAB_CLICK = 'shef-dashboard-my-ratings-tab-click',
  SHEF_DASHBOARD_SEE_YOUR_REVIEWS_BTN_CLICK = 'shef-dashboard-see-your-reviews-btn-click',
  SHEF_DASHBOARD_PROMOTE_YOURSELF_COPY_LINK_CLICK = 'shef-dashboard-promote-yourself-copy-link-click',
  SHEF_INSIGHTS_FEEDBACK_CLICK = 'shef-insights-feedback-click',
  SHEF_INSIGHTS_REPORT_HELPFUL_VOTE = 'shef-insights-report-helpful-vote',
  SHEF_DASHBOARD_ORDERS_BANNER_CLICK = 'shef-dashboard-orders-banner-click',
  SHEF_DASHBOARD_REFERRAL_BANNER_CLICK = 'shef-dashboard-referral-banner-click',
  SHEF_DASHBOARD_REFERRAL_DOWNLOAD_AD_CLICK = 'shef-dashboard-referral-download-ad-click',
  SHEF_DASHBOARD_REFERRAL_MENU_LINK_CLICK = 'shef-dashboard-referral-menu-link-click',
  SHEF_DASHBOARD_REFERRAL_HOW_IT_WORKS_LINK_CLICK = 'shef-dashboard-referral-how-it-works-link-click',
  SHEF_DASHBOARD_REFERRAL_SHARE_TO_FACEBOOK_CLICK = 'shef-dashboard-referral-share-to-facebook-click',
  SHEF_DASHBOARD_REFERRAL_SHARE_TO_WHATSAPP_CLICK = 'shef-dashboard-referral-share-to-whatsapp-click',
  SHEF_DASHBOARD_REFERRAL_SHARE_TO_TWITTER_CLICK = 'shef-dashboard-referral-share-to-twitter-click',
  SHEF_DASHBOARD_REFERRAL_COPY_REFERRAL_CODE_CLICK = 'shef-dashboard-referral-copy-referral-code-click',
  SHEF_DASHBOARD_GROW_YOUR_BUSINESS_TAB_CLICK = 'shef-dashboard-grow-your-business-tab-click',
  SHEF_DASHBOARD_GROW_YOUR_BUSINESS_TAKE_ME_THERE = 'shef-dashboard-grow-your-business-tab-take-me-there',
  SHEF_DASHBOARD_GIVE_GET_PANEL_DETAILS_CLICK = 'shef-dashboard-give-get-panel-details-click',
  SHEF_DASHBOARD_GIVE_GET_PANAL_SHARE_LINK_CLICK = 'shef-dashboard-give-get-panel-share-link-click',
  SHEF_DASHBOARD_GIVE_GET_PANAL_COPY_LINK_CLICK = 'shef-dashboard-grow-your-business-copy-link-click',
  SHEF_DASHBOARD_GROW_YOUR_BUSINESS_SHEF_TO_SHEF = 'shef-dashboard-grow-your-business-shef-to-shef',
  SHEF_PRICE_OUT_OF_RANGE_MODAL = 'shef-price-out-of-range-modal',

  // Shef WSS Merch Landing Page
  SHEF_WSS_MERCH_TAB_VIEW = 'shef-wss-merch-tab-view',
  SHEF_WSS_MERCH_MODAL_VIEW = 'shef-wss-merch-modal-view',
  SHEF_WSS_MERCH_TAB_ADD_DISH = 'shef-wss-merch-tab-add-dish',
  SHEF_WSS_SEASONAL_HANDOFF_BANNER_VIEW = 'shef-wss-seasonal-handoff-banner-view',
  SHEF_WSS_SEASONAL_HANDOFF_BANNER_CLICK = 'shef-wss-seasonal-handoff-banner-click',

  CLICK_HELP_WIDGET = 'click-help-widget',

  // Celebrity Events
  CELEBRITY_EVENT_ERROR = 'celebrity-event-error',

  // 1/10/23 OneSignal push notifications – deprecated. Remove after no events have been logged for a while.
  WEB_PUSH_SUBSCRIPTION_CHANGE = 'web-push-subscription-change',
  WEB_PUSH_PERMISSION_PROMPT_DISPLAY = 'web-push-permission-prompt-display',
  WEB_PUSH_NOTIFICATION_PERMISSION_CHANGE = 'web-push-notification-permission-change',
  WEB_PUSH_POPOVER_SHOWN = 'web-push-popover-shown',
  WEB_PUSH_CUSTOM_PROMPT_CLICK = 'web-push-custom-prompt-click',
  WEB_PUSH_NOTIFICATION_DISPLAY = 'web-push-notification-display',
  WEB_PUSH_NOTIFICATION_DISMISS = 'web-push-notification-dismiss',

  REVIEW_GROUP_ORDER_BUTTON_CLICK = 'review-group-order-button-click',
  GROUP_ORDER_RATING_MODAL_SHOW = 'group-order-rating-modal-show',
  GROUP_ORDER_RATING_MODAL_STEP = 'group-order-rating-modal-step',
  GROUP_ORDER_RATING_MODAL_PARTIAL_SAVE = 'group-order-rating-modal-partial-save',
  GROUP_ORDER_RATING_MODAL_SUBMIT = 'group-order-rating-modal-submit',
  GROUP_ORDER_RATING_MODAL_SUBMIT_SUCCESS = 'group-order-rating-modal-submit-success',
  GROUP_ORDER_RATING_MODAL_SUBMIT_FAILURE = 'group-order-rating-modal-submit-failure',
  RATE_RECENT_GROUP_ORDER_MODAL_SHOW = 'rate-recent-group-order-modal-show',
  RATE_RECENT_GROUP_ORDER_MODAL_CLICK = 'rate-recent-group-order-modal-click',

  WAITLIST_ACTION_CLICKED = 'waitlist-action-clicked',

  BROWSER_TRACKER_RECONCILIATION = 'browser-tracker-reconciliation',
  REFERRAL_REDIRECT = 'referral-redirect',

  FOOD_ITEM_PRICE_RECOMMENDATION = 'food-item-price-recommendation',

  FOOD_ITEM_VALIDATION_AUTO_FIX = 'food-item-validation-auto-fix',
  FOOD_ITEM_VALIDATION_UNAVAILABLE = 'food-item-validation-unavailable',
  FOOD_ITEM_VALIDATION_DISH_SUBMITTED = 'food-item-validation-dish-submitted',

  SHEF_MENU_NAV_CLICK = 'shef-menu-nav-click',

  /**
   * Client events that are specific only to the native app.
   *
   * DO NOT USE THESE EVENTS FOR SHEF-MONO.
   *
   * We need this here for now until we figure out a way to leverage the same analytics code
   * for both shef-mono and consumer-app.
   */
  TRACKING_PERMISSION_REQUESTED = 'tracking-permission-requested',

  PUSH_NOTIFICATION_OPEN = 'push-notification-open',
  PUSH_NOTIFICATION_PROMPT_SHOW = 'push-notification-prompt-show',
  PUSH_NOTIFICATION_PROMPT_HIDE = 'push-notification-prompt-hide',
  PUSH_NOTIFICATION_PERMISSION_REQUESTED = 'push-notification-permission-requested',

  APP_REVIEW_REQUEST_ATTEMPT = 'app-review-request-attempt',
  APP_REVIEW_REQUEST_FAILED = 'app-review-request-failed',
  APP_REVIEW_REQUEST_SKIPPED = 'app-review-request-skipped',
  APP_REVIEW_REQUESTED = 'app-review-requested',

  LOCATION_SOURCE_SELECTED = 'location-source-selected',
  LOCATION_PERMISSION_REQUESTED = 'location-permission-requested',
  LOCATION_SET = 'location-set',

  PRE_CHECKOUT_UPSELL_MODAL_VIEW = 'pre-checkout-upsell-modal-view',
  PRE_CHECKOUT_UPSELL_MODAL_CLOSE = 'pre-checkout-upsell-modal-close',
  PRE_CHECKOUT_UPSELL_ADD_ALL = 'pre-checkout-upsell-add-all',
  PRE_CHECKOUT_UPSELL_REFRESH_SUGGESTIONS = 'pre-checkout-upsell-refresh-suggestions',
  /** End native app events */

  // Meal Plan Cancellation Events
  CANCEL_REASONS_SUBMITTED = 'cancel-reasons-submitted',
  CANCEL_FREE_FORM_STEP_SUBMITTED = 'cancel-free-form-step-submitted',
  SUBSCRIPTION_CANCELLATION_REASON_CAPTURED = 'subscription-cancellation-reason-captured',
  CANCEL_REASONS_ACTION_CLICK = 'cancel-reasons-action-click',
  MEAL_PLAN_CANCELLATION_WARNING_SHOWN = 'meal-plan-cancellation-warning-shown',
  MEAL_PLAN_CANCELLATION_WARNING_DISMISSED = 'meal-plan-cancellation-warning-dismissed',
  MEAL_PLAN_CANCELLATION_WARNING_BYPASSED = 'meal-plan-cancellation-warning-bypassed',

  // Self-reported attribution
  SELF_REPORTED_ATTRIBUTION = 'self-reported-attribution',
  SELF_REPORTED_ATTRIBUTION_SHOWN = 'self-reported-attribution-shown',

  // CCPA Consent
  CCPA_CONSENT_BANNER_SHOWN = 'ccpa-consent-banner-shown',
  CCPA_CONSENT_ACCEPTED = 'ccpa-consent-accepted',
  CCPA_CONSENT_WITHDRAWN = 'ccpa-consent-withdrawn',
  CCPA_AD_TRACKERS_INITIALIZED = 'ccpa-ad-trackers-initialized',
  CCPA_FOOTER_LINK_CLICKED = 'ccpa-footer-link-clicked',
  CCPA_CUSTOMIZE_CLICKED = 'ccpa-customize-clicked',
  CCPA_CUSTOM_PREFERENCES_SAVED = 'ccpa-custom-preferences-saved',

  // 3P landing pages (shef-tag.js on shef.com/lp/<slug>/)
  LP_PAGE_VIEW = 'lp-page-view',
  LP_SESSION_STARTED = 'lp-session-started',
  LP_ZIP_SUBMIT = 'lp-zip-submit',
  LP_CTA_CLICK = 'lp-cta-click',

  // TableRun shef hub — one-tap enable subscriptions at the suggested price
  TABLERUN_SUGGESTED_PRICE_SHOWN = 'tablerun-suggested-price-shown',
  TABLERUN_SUGGESTED_PRICE_TAPPED = 'tablerun-suggested-price-tapped',
  TABLERUN_ENABLE_SUBSCRIPTIONS = 'tablerun-enable-subscriptions',
  TABLERUN_DISABLE_SUBSCRIPTIONS = 'tablerun-disable-subscriptions',
}

export enum DeprecatedClientEvents {
  SEARCH = 'search',
  SELECT_DELIVERY_DAY_SHEF = 'select-delivery-day-shef',
  SEARCH_TEXT_CHANGED = 'search-text-changed',
}

export interface DeprecatedClientTrackableEvents
  extends Record<DeprecatedClientEvents, JSONObj>,
    TrackableSharedEvents {
  // Description: ?
  // Deprecated (when?)
  [DeprecatedClientEvents.SEARCH_TEXT_CHANGED]: {
    text: string;
  };
  // Description: ?
  // Deprecated.
  [DeprecatedClientEvents.SELECT_DELIVERY_DAY_SHEF]: {
    shefId: string;
    date: string;
  };
  [DeprecatedClientEvents.SEARCH]: {
    // Deprecated: 2020-03-3
    // Description: Use to be fired when a user entered in their zipcode on our landing page. This is now LANDING_ZIP_SUBMIT
    value: string;
    page: string;
  };
}

export type PurchaseData = {
  rank?: number;
  esid?: string; // explore segment id
  promoCode?: string;
  promoCodeType?: string;
  promoCodeReferralType?: 'consumer' | 'shef';
  promoCodeValue?: number;
  promoCodeIsPercentage?: boolean;
  promoCodeDescription?: string;
  // shefId: string; -- deprecated 2022-10-10
  shefIds: string[];
  subtotal: number;
  shefTip: number | null;
  paymentMethod: string;

  cuisineRegion?: string | null;
  organizationName: string | null;
  // orderId?: number; -- deprecated 2022-10-10
  groupOrderId: string;
  orderNumber: number;
};

type PurchaseItemData = {
  foodItemId: number;
  shefId: string;
  quantity: number;
};

export interface ClientTrackableEvents
  extends Record<ClientEvents, ExceptFields<JSONObj, 'source'>>,
    TrackableSharedEvents {
  [ClientEvents.CHECKOUT_DELIVERY_ADDRESS_SELECT]: JSONObj;
  [ClientEvents.CHECKOUT_DELIVERY_TIME_SELECT]: JSONObj;
  [ClientEvents.CHECKOUT_CREDIT_CARD_INPUT]: JSONObj;
  [ClientEvents.CHECKOUT_TIP_VALUE_UPDATE]: {
    tipValue: number;
  };
  [ClientEvents.AUTO_APPLIED_REWARD_PROMO]: {
    code: string;
    promoCodeType: string;
    value: number;
    isPercentage: boolean;
    promoCodeDescription: string;
  };
  [ClientEvents.CHECKOUT_ORDER_ERROR]: {
    errors: string[];
    productType: 'marketplace' | 'subscriptions';
  };
  [ClientEvents.CHECKOUT_LEGAL_PROMPT]: {
    hasTermsOfService: boolean;
    hasSmsMarketingOptIn: boolean;
  };
  // Description: When user attempts to sign up from the register page.
  [ClientEvents.LOGIN_MODAL_SIGNUP_SUBMIT]: {
    method: AuthMethod;
  };
  [ClientEvents.SIGN_UP_ERROR]: {
    // Description: When user receives an error while registering
    method: AuthMethod; // Google, Facebook, Email etc.
    errors: string[]; // user-facing error message
  };
  [ClientEvents.FOOD_SEARCH]: {
    // Description:
    value: string; // Current text in field
    numResults: number; // TODO: How many results were returned. Currently hard-coded to 0.
  };
  [ClientEvents.FOOD_SEARCH_RESULTS_DISPLAYED]: {
    // Description: Tracks the search results shown
    query: string; // Search term / query string
    shef_results: string[];
    food_item_results: number[];
    cuisine_results: string[];
  };
  [ClientEvents.SHEF_SEARCH_RESULT_CLICK]: {
    // Description: When user clicks on a search result that is a Shef
    shefId: string;
    search: string; // User's search query
  };
  [ClientEvents.FOOD_SEARCH_RESULT_CLICK]: {
    // Description: When user clicks on a search result that is a dish.
    foodItemId: number;
    foodName: string | null;
    search: string; // User's search query
  };
  [ClientEvents.CUISINE_SEARCH_RESULT_CLICK]: {
    // Description: When user clicks on a search result that is a cuisine.
    cuisineCategoryId: string;
    search: string; // User's search query
  };
  [ClientEvents.SEARCH_SHOW_MORE_SHEFS_CLICK]: {
    // Description: When user clicks "show more" shef results
    search: string; // User's search query
  };
  [ClientEvents.SEARCH_SHOW_MORE_DISHES_CLICK]: {
    // Description: When user clicks "show more" dish results
    search: string; // User's search query
  };
  [ClientEvents.MORE_CUISINES_CLICK]: {
    cuisineCategoryId: string;
    ancestorCuisineCategoryId: string;
  };
  [ClientEvents.GIFT_CARD_SEARCH_RESULT_CLICK]: {
    search: string; // User's search query
  };
  // Description: When a user clicks a trending search from the search dropdown
  [ClientEvents.TRENDING_SEARCH_CLICK]: {
    query: string; // Trending search term
  };
  [ClientEvents.LOGIN_MODAL_FORGOT_PASSWORD_CLICKED]: {
    signUpEntryPoint?: string | null;
    gateType?: LoginGateType;
  };
  // Description: When user adds something to the cart
  [ClientEvents.CART_ADD_ITEM]: {
    id: number;
    price: number;
    foodType: string | null;
    vegan: boolean | null;
    vegetarian: boolean | null;
    lowFodmap: boolean | null;
    paleo: boolean | null;
    whole30: boolean | null;
    glutenFree: boolean | null;
    dairyFree: boolean | null;
    containsNuts: boolean | null;
    isPopular: boolean | null;
    compostable: boolean;
    servingSizeCustomizationIds: string[];
    spiceLevelCustomizationIds: string[];

    location?: string;
  };

  // Description: When user removes something from their cart
  [ClientEvents.CART_REMOVE_ITEM]: {
    id: number;
    price: number;
    foodType: string | null;
    vegan: boolean | null;
    vegetarian: boolean | null;
    lowFodmap: boolean | null;
    paleo: boolean | null;
    whole30: boolean | null;
    glutenFree: boolean | null;
    dairyFree: boolean | null;
    containsNuts: boolean | null;
    isPopular: boolean | null;
    compostable: boolean;
    servingSizeCustomizationIds: string[];
    spiceLevelCustomizationIds: string[];

    location?: string;
  };
  // Description: When user adds something to their cart via the pop-up modal to get them to $25 free delivery
  [ClientEvents.FREE_DELIVERY_ADD_ITEM]: Partial<{
    id: number;
    price: number;
    foodType: string | null;
    vegan: boolean | null;
    vegetarian: boolean | null;
    lowFodmap: boolean | null;
    paleo: boolean | null;
    whole30: boolean | null;
    glutenFree: boolean | null;
    dairyFree: boolean | null;
    containsNuts: boolean | null;
    isPopular: boolean | null;
  }>;
  // Description: When user purchases an item
  [ClientEvents.PURCHASE]: PurchaseData & {
    items: PurchaseItemData[];
  };
  [ClientEvents.EXISTING_USER_PURCHASE]: PurchaseData;
  [ClientEvents.NEW_USER_PURCHASE]: PurchaseData;
  // Description: When user clicks the purchase button. Before errors are shown
  [ClientEvents.PURCHASE_ATTEMPT]: {
    rank?: number;
    esid?: string; // explore segment id
    promoCode?: string;
    promoCodeType?: string;
    promoCodeIsPercentage?: boolean;
    promoCodeValue?: number;
    promoCodeDescription?: string;
    shefIds: string[];
    subtotal: number;
    shefTip: number | null;
    paymentMethod: string;

    cuisineRegion?: string | null;
    organizationName: string | null;
  };
  // Description: When user clicks the purchase button. Before errors are shown
  [ClientEvents.PURCHASE_ATTEMPT_FAILED]: {
    rank?: number;
    esid?: string; // explore segment id
    promoCode?: string;
    promoCodeType?: string;
    promoCodeIsPercentage?: boolean;
    promoCodeValue?: number;
    promoCodeDescription?: string;
    shefIds: string[];
    subtotal?: number;
    shefTip?: number | null;
    paymentMethod: string;
    productType: 'marketplace' | 'subscriptions';
    cuisineRegion?: string | null;
    organizationName?: string | null;

    community?: string;
    discounts?: number;
    addOnsTotal?: number;
    taxesAndFees?: number;
    taxes?: number;
    fees?: number;
    finalPrice?: number;

    badUserInput?: boolean;
    errorMessage?: string;
  };
  // Description: Fired client side after a shef application is sucessfully submitted
  [ClientEvents.SHEF_APPLICATION_RECEIVED]: {
    firstName: string;
    lastName?: string | null;
    zipcode: string;
    email: string;
    phone: string;
    cuisines?: string | null;
    referral?: string | null;
  };
  // Description: Fired client side when a user starts the typeform questionnaire in the shef application flow
  [ClientEvents.SHEF_MEAL_PLAN_APPLICATION_STARTED]: {};
  // Description: Fired client side when a user completes the questionnaire step and proceeds to the menu step
  [ClientEvents.SHEF_MEAL_PLAN_QUESTIONNAIRE_COMPLETED]: {};
  // Description: Fired client side when a user completes the entire shef application (questionnaire + menu)
  [ClientEvents.SHEF_MEAL_PLAN_APPLICATION_COMPLETED]: {};
  // Description: Fired when a shef in a hub market picks a non-hub fulfillment option on the dropoff
  // step — the NY Shef Pickup Program (SPP) or the "none of these work" checkbox in a non-NY hub market
  [ClientEvents.SHEF_ONBOARDING_NON_HUB_OPTION_SELECT]: { userId: string; option: 'SPP' | 'None' };
  // Description: Fired client side when a user lands on our website with a URL from a valid organization (company)
  [ClientEvents.ORGANIZATION_LANDING]: {
    zipcode?: string;
    organizationName: string;
  };
  // Description: When a referral code is copied to the clipboard
  [ClientEvents.REFERRAL_CODE_COPIED]: {
    code: string;
  };
  // Description: When a WSSReferralAdModal is viewed
  [ClientEvents.REFERRAL_MEAL_PLAN_MODAL_VIEW]: {
    location: ReferralShareLocation;
  };
  // Description: When a user's web page has been automatically reloaded due to out of sync code.
  [ClientEvents.API_SYNC_RELOAD]: JSONObj;
  // Description: When user has clicked to share a referral code. We do not know if they completed the action
  // as that's in the native share widget / on a separate webpage.
  [ClientEvents.REFERRAL_CODE_SHARED]: {
    code: string;
    shefId?: string;
    type: ShareType; // e.g. FB, twitter, etc.
    location: ReferralShareLocation; // Location it's shared from
  };
  // Description: When a shef has clicked to share a food item.
  [ClientEvents.SHARE_SHEF_FOOD_ITEM]: {
    foodItemId: number;
    shefId: string;
  };
  // Description: When a shef has clicked to share a link to their menu.
  [ClientEvents.SHARE_SHEF_MENU]: {
    shefId: string;
  };
  // Description: When a shef has clicked to share a custom promo code they made.
  [ClientEvents.SHARE_SHEF_PROMO_CODE]: {
    shefId: string;
    code: string;
    promoCodeIsPercentage: boolean;
    value: number;
    promoCodeDescription: string;
  };
  // Ignore.
  [ClientEvents.SHARED_NONE]: JSONObj;

  // Description: When a user session has started. Sessions are at most 30 minutes, and per browser tab.
  [ClientEvents.SESSION_STARTED]: {
    startedAt: number;
    referrer: string | undefined;
    gclid: string | undefined;
    language: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    utmTarget?: string;
    sessionId: string;
  };
  // Description: When a user has landed on the shef application page
  [ClientEvents.BECOME_A_SHEF_VISIT]: JSONObj;

  /** A URL no consumer route matched, which renders the not-found page instead of the homepage. */
  [ClientEvents.PAGE_NOT_FOUND]: {
    /** The full URL as requested, host and query string included, so broken links can be traced by their UTM params. */
    url: string;
    path: string;
    /** `document.referrer`, or null when the visitor arrived with none. */
    referrer: string | null;
  };

  // Description: All page views
  [ClientEvents.PAGE_VIEW]: {
    // Description: the full path of the current URL, like /explore/zip/91290?utm_campaign=new
    route: string;
    queryParams: JSONType | null;

    // Description: "92190" for the URL "/explore/zip/91290" when matching the pattern in "/explore/zip/:zip_code"
    params: JSONType | null;

    // Description: the URL pattern we used to route the consumer with, like "/explore/zip/:zip_code"
    pattern: string | null;

    // Description: the preferred color theme of the user's browser on page view.
    preferredColorTheme: ColorTheme | null;

    // Description: Dimensions of the user's viewport
    viewportDimensions: {
      widthPixels: number;
      heightPixels: number;
    };
  };
  // Description: user has changed the delivery days they want to filter results for.
  [ClientEvents.SUBMIT_DELIVERY_DAY_FILTER]: {
    dates: string[];
  };
  // Description: user opened the dietary filter options.
  [ClientEvents.CLICK_DIETARY_FILTER]: JSONObj;
  // Description: user has changed the dietary restrictions they want to filter results for.
  [ClientEvents.SUBMIT_DIETARY_FILTER]: {
    dietaryTags: string[];
  };
  // Description: user clicks on spice level filter options
  [ClientEvents.CLICK_SPICE_FILTER]: JSONObj;
  // Description: user has changed their spice level filters
  [ClientEvents.SUBMIT_SPICE_FILTER]: {
    spiceLevels: string[];
  };
  // Description: user has changed the cuisine categories they want to filter results for.
  [ClientEvents.SUBMIT_CUISINE_CATEGORY_FILTER]: {
    cuisineCategories: string[];
  };
  // Description: user has changed their cuisine filter
  [ClientEvents.SELECT_CUISINE_FILTER]: {
    segmentFilterSelected: string | null;
    subcuisineFilterSelected?: string | null;
    elementName?: string | null;
  };

  // Description: user clicks on pickup distance filter options
  [ClientEvents.CLICK_PICKUP_DISTANCE_FILTER]: Record<string, never>;
  // Description: user has changed their pickup distance filter
  // deprecated 11-29-2022
  // [ClientEvents.SUBMIT_PICKUP_DISTANCE_FILTER]: {
  //   pickupDistance: number | null;
  // };

  // Description: When user clicks the < and > arrows on a shef on the explore page to see other dishes.
  [ClientEvents.CAROUSEL_SWIPE]: {
    direction: string;
  };
  // Description: When user submits a zipcode on the landing page
  [ClientEvents.LANDING_ZIP_SUBMIT]: {
    zip: string;
    queryParams?: Record<string, string>;
  };
  // Description: When user submits a zipcode on the explore page
  [ClientEvents.EXPLORE_ZIP_SUBMIT]: {
    zip: string;
    destination?: 'meal-plan-intake' | 'explore-one-time';
  };
  // Description: When user clicks to watch a video on a shef's profile
  [ClientEvents.VIDEO_BIO_CLICK]: {
    isCoverImage: boolean;
    shefId: string;
  };
  // Description: When user clicks the "more" link in a shef's bio to read the full bio.
  [ClientEvents.LEARN_MORE_CLICK]: {
    shefId: string;
  };

  // Description: When user clicks the + icon on a food item to increase quantity in cart
  [ClientEvents.CART_INCREMENT]: {
    shefId: string | null;
    itemId: string | number;
  };
  // Description: When user clicks the - icon on a food item to decrease quantity in cart
  [ClientEvents.CART_DECREMENT]: {
    shefId: string | null;
    itemId: string | number;
  };
  // Description: When user clicks a card in the shef spotlight section
  [ClientEvents.SHEF_SPOTLIGHT_CLICK]: {
    shefId: string | null;
  };
  // Description: When user clicks a card in the recently reviewed section
  [ClientEvents.RECENTLY_REVIEWED_CLICK]: {
    shefId: string | null;
  };

  // Description: When user clicks on the final email login page.
  [ClientEvents.LOGIN_MODAL_EMAIL_LOGIN_SUBMIT]: {
    method: AuthMethod;
  };

  // Description: When user fails to login.
  [ClientEvents.LOGIN_FAILED]: {
    method?: AuthMethod;
    errorMessage: string;
  };

  // Description: When a request for single-sign on authentication has succeeded.
  [ClientEvents.SSO_AUTH_SUCCESS]: {
    method: AuthMethod;
  };

  /**
   * Description: Lifecycle of the Google One Tap auto-prompt, reported by Google's
   * prompt() notification. `displayed` is an impression; `notDisplayedReason` /
   * `skippedReason` explain why it didn't show (e.g. `browser_not_supported`,
   * `opt_out_or_no_session`, cooldown), and a `dismissed` moment with
   * `dismissedReason === 'credential_returned'` is an accepted prompt.
   */
  [ClientEvents.GOOGLE_ONE_TAP_PROMPT_MOMENT]: {
    momentType: 'display' | 'skipped' | 'dismissed';
    displayed: boolean;
    notDisplayedReason?: string | null;
    skippedReason?: string | null;
    dismissedReason?: string | null;
  };

  // Description: When the user accepts Google One Tap and Google returns a credential (the "click").
  [ClientEvents.GOOGLE_ONE_TAP_CREDENTIAL_RETURNED]: Record<string, never>;

  // Description: When user clicks on hambuger menu in mobile
  [ClientEvents.NAV_HAMBUGER_MENU_CLICK]: Record<string, never>;

  // Description: When user clicks on an item in the nav bar
  [ClientEvents.NAV_CLICK]: {
    elementName: NavClickElementName;
  };

  // Description: When user visits a menu page
  [ClientEvents.SHEF_MENU_VISIT]: Record<string, never>;

  // Description: When user visits a menu item dialog
  // Location is expected to be populated on meal plan experience, and null elsewhere for now
  [ClientEvents.DISH_MODAL_VISIT]: {
    type: 'description-legacy' | 'customizations' | 'dish-first';
    productType: 'subscriptions' | 'marketplace';
    dishServingSizeOptionsCount: number;
    dishSpiceLevelOptionsCount: number;
    foodItemId: number;
    location: string | null;
    collectionId?: string;
    cuisines?: string;
  };

  // Description: When deciding which dish modal variant to render
  [ClientEvents.DISH_MODAL_VARIANT_DECISION]: {
    experimentName: ExperimentNames;
    variant: string;
    isTreatment: boolean;
    willRenderRevamp: boolean;
    productType: 'subscriptions' | 'marketplace';
    foodItemId: number;
    location: string | null;
    collectionId?: string;
  };

  // Description: When user clicks the Shef profile image on a menu page (mobile only)
  [ClientEvents.SHEF_PROFILE_IMAGE_CLICK]: {
    shefId: string;
  };

  // Description: When user clicks the food image on a menu item dialog (mobile only)
  [ClientEvents.DISH_IMAGE_CLICK]: {
    foodItemId: number;
  };

  // Description: When user clicks on a prefab food item
  [ClientEvents.PREFAB_FOOD_ITEM_COPY]: {
    originalFoodItem: JSONObj;
    newFoodItem: JSONObj;
    shefId?: string;
    searchValue?: string;
    suggestionRank?: number;
  };

  [ClientEvents.REORDER_BUTTON_CLICK]: {
    shefId: string;
    orderId: number;
  };

  [ClientEvents.RECURRING_SUBSCRIPTION_OPTION_SELECT]: {
    shefId?: string;
    recurringPeriod: string;
  };

  [ClientEvents.RECURRING_SUBSCRIPTION_CANCEL_BUTTON_CLICK]: {
    subscriptionId?: string;
  };

  [ClientEvents.RECURRING_SUBSCRIPTION_SKIP_ORDER_BUTTON_CLICK]: {
    groupOrderId: string;
  };

  // Description: When user submits filters on Filters modal
  [ClientEvents.SUBMIT_DISH_FILTER]: {
    cuisineFilters: string[] | undefined;
    dietaryFilters: string[] | undefined;
    isEcoFriendlyPackaging: boolean | undefined;
    minRatingFilter: number | undefined;
    priceLevelFilters: string[] | undefined;
    proteinFilters: string[] | undefined;
    segmentFilters: string[] | undefined;
    spiceLevelFilters: string[] | undefined;
  };

  // Description: When user clicks on a collection in the "Browse by collection" section
  [ClientEvents.BROWSE_BY_TAGS_CLICK]: {
    tag: string;
    type: BrowseByTagsType;
    position: number;
  };

  // Description: When user clicks on a collection in the "Browse by meal type" section
  [ClientEvents.BROWSE_BY_MEAL_TYPE_CLICK]: {
    location: string;
    mealType: string;
    position: number;
    segmentFilters: string[] | undefined;
    pattern: string | null;
  };

  // Description: Triggers when user click the browse by shef mini banner
  [ClientEvents.BROWSE_BY_SHEF_CLICK]: Record<string, never>;

  // Description: Triggers when user click the bulk discount explore banner
  [ClientEvents.BULK_DISCOUNT_EXPLORE_CLICK]: Record<string, never>;

  // Description: Triggers when user sees tbe bulk discount modal
  [ClientEvents.BULK_DISCOUNT_MODAL_VIEW]: Record<string, never>;

  // Description: Triggers when user hides the bulk discount modal
  [ClientEvents.BULK_DISCOUNT_MODAL_CLOSE]: Record<string, never>;

  // Description: When user clicks on the scroll arrow in the date picker
  [ClientEvents.DELIVERY_DAY_FILTER_ARROW_CLICK]: {
    direction: 'right' | 'left';
  };

  // Description: When user clicks on segment arrows on explore
  [ClientEvents.SEGMENT_ARROW_CLICK]: {
    direction: 'right' | 'left';
    elementName: string;
  };

  // Description: When user clicks on the See All button on an explore segment
  [ClientEvents.SEGMENT_SEE_ALL_LINK_CLICK]: {
    segmentId: string;
    segmentRenderedItems: number;
    segmentTotalItems: number;
    position: number;
    type: 'shef' | 'dish';
    filters: {
      spiceFilters: string[] | undefined | null;
      cuisineFilters: string[] | undefined | null;
      dietaryFilters: string[] | undefined | null;
      dishTypeFilters: string[] | undefined | null;
      priceFilters: string[] | undefined | null;
      dateFilters: string[] | undefined | null;
      proteinFilters: string[] | undefined | null;
    };
  };
  [ClientEvents.SEGMENT_ITEM_CLICK]: {
    segmentId: string;
    segmentRenderedItems: number;
    segmentTotalItems: number;
    position: number;
    type: 'shef' | 'dish';
    rank: number;
    filters: {
      spiceFilters: string[] | undefined | null;
      cuisineFilters: string[] | undefined | null;
      dietaryFilters: string[] | undefined | null;
      dishTypeFilters: string[] | undefined | null;
      priceFilters: string[] | undefined | null;
      dateFilters: string[] | undefined | null;
      proteinFilters: string[] | undefined | null;
    };
  };

  [ClientEvents.DISH_DETAILS_SHEF_MENU_SEE_ALL_CLICK]: Record<string, never>;

  [ClientEvents.EXPLORE_SEE_MORE_CLICK]: Record<string, never>;

  [ClientEvents.EXPLORE_FOOD_ITEMS_RENDERED]: {
    // Description: When an explore segment is being sent down to the client to be rendered.
    // If any segment filters were applied at the time of rendering
    segmentFilters: string[] | undefined;
    // The segment id, e.g. shef-of-the-week, next-day, etc
    esid: string;
    // The position that the section should appear on the client (assuming we
    // render the segments in order). Offset is added to make position independent
    // of someone using infinite scroll or not
    pos: number;
    // The SegmentLayout used. e.g. GENERIC, HORIZONTAL_SCROLL, etc
    layout: string;
    // List of items rendered in the segment
    itemIds: string[];
    // The total number of possible elements we can show (esti)
    total: number;
    // Whether or not the segment is for dishes
    isDishSegment: boolean;
    // The current delivery day
    deliveryDay: string;
  };

  [ClientEvents.DEVICE_ZIP_TIME_ZONE_DIFFERENCE]: {
    zipCode: string;
    zipCodeTimeZone: string;
    deviceTimeZone: string;
    offset: number; // ms offset between the two timezones
  };

  // Description: When user views suggested prefab food items
  [ClientEvents.FOOD_ITEM_SUGGESTIONS_VIEW]: {
    shefId: string;
    foodItemSuggestions: {
      foodItemId: number;
      foodItemName?: string;
      suggestionRank: number;
    }[];
  };

  [ClientEvents.AUTO_APPLIED_PROMO]: {
    promoCode: string;
    promoCodeType: string;
    promoCodeIsPercentage: boolean;
    value: number;
    promoCodeDescription: string;
  };

  // Description: A promo code has been applied from the url.
  [ClientEvents.AUTO_APPLIED_SUBSCRIPTION_PROMO]: {
    promoCode: string;
    promoCodeIsPercentage: boolean;
    value: number;
    promoCodeDescription: string;
  };
  // Description: Fires each time the side pairing modal is opened (by ATC action)
  [ClientEvents.MEAL_PLAN_SIDE_MODAL_SHOWN]: {
    mainFoodItemId: number;
    format: string;
  };

  // Description: Fires once for each shef rendered in the sides modal, with a property with an array of food items.
  [ClientEvents.MEAL_PLAN_SIDE_MODAL_SHEF_RENDERED]: {
    shefId: string;
    mainFoodItemId: number;
    path: string;
    foodItemIds: number[];
    format: string;
  };

  // Description: Fires each time user clicks the “review order”
  // CTA on dish selection, which brings up the review order view
  [ClientEvents.MEAL_PLAN_REVIEW_ORDER_SHOWN]: {
    format: string;
    nativePayAvailable: 'apple' | 'google' | 'none';
    mainServings?: number;
    flexServings?: number;
  };
  // Description: Fires when the flex-servings "add more servings for $X/serving" cart status becomes visible.
  [ClientEvents.MEAL_PLAN_FLEX_SERVINGS_UPSELL_SHOWN]: {
    mainServings: number;
    flexServings: number;
    pricePerServingCents: number;
  };
  // Description: Fires once when the mains cart first reaches the minimum plan size, in both the
  // control and flex-servings arms, to measure drop-off between a filled mains cart and the sides step.
  [ClientEvents.MEAL_PLAN_MAINS_CART_FILLED]: {
    totalNumServings: number;
    numServingsUnits: number;
    numMeals: number;
  };
  /** An Add-Ons pill tap. `categoryPosition` is 0-based. */
  [ClientEvents.MEAL_PLAN_ADD_ONS_CATEGORY_SELECTED]: {
    categoryId: string;
    categoryName: string;
    categoryPosition: number;
  };
  /**
   * Fires once per Add-Ons mount, when the Desserts section is half in view. `arrivalMethod` tells
   * banner arrivals from Desserts pill taps and plain scrolling; `scroll` also counts a section
   * already half in view on load, which is most treatment views without kids. The position is 0-based.
   */
  [ClientEvents.MEAL_PLAN_DESSERT_SECTION_VIEWED]: {
    foodItemIds: number[];
    arrivalMethod: DessertSectionArrivalMethod;
    sectionPosition: number;
  };
  [ClientEvents.MEAL_PLAN_DESSERT_BANNER_SHOWN]: { deliveryDate: string; numDessertsAvailable: number };
  [ClientEvents.MEAL_PLAN_DESSERT_BANNER_CLICK]: {
    deliveryDate: string;
    destination: 'add-ons-desserts';
    numDessertsAvailable: number;
  };
  // Description: Fires once per view of the intake delivery-day step, with the day-of-week options offered.
  [ClientEvents.MEAL_PLAN_DELIVERY_DAY_SHOWN]: {
    availableDOW: string[];
  };

  // Description:Fires if user exits out of review order without proceeding to checkout page
  [ClientEvents.MEAL_PLAN_REVIEW_ORDER_CLOSED]: Record<string, never>;

  // Description: fires each time user clicks the “edit” button in the review order view
  [ClientEvents.MEAL_PLAN_REVIEW_ORDER_EDIT_CLICK]: Record<string, never>;

  // Description: fires when user clicks “Continue to checkout” button on review order
  [ClientEvents.MEAL_PLAN_REVIEW_ORDER_CHECKOUT_CLICK]: {
    nativePayAvailable: 'apple' | 'google' | 'none';
  };

  // Description: fires every time user interacts with the mains / sides tab
  [ClientEvents.MEAL_PLAN_DISH_TYPE_NAVIGATION]: {
    dishType: string;
    path: string;
    format: string;
  };

  [ClientEvents.MEAL_PLAN_SIDE_MODAL_DISMISSED]: {
    action: 'minimize' | 'close';
    method: 'scroll' | 'click-or-swipe';
  };

  // Description: Fires when a main has no recommended side pairings (empty carousel)
  [ClientEvents.MEAL_PLAN_SIDES_PAIRING_EMPTY_CAROUSEL]: {
    mainFoodItemId: number;
  };

  // Description: Fires when user clicks "Browse all sides" on empty pairing CTA
  [ClientEvents.MEAL_PLAN_SIDES_PAIRING_BROWSE_ALL_SIDES_CLICK]: {
    mainFoodItemId: number;
  };

  // Description: Fires when user clicks "Shop all sides" on the explore more CTA
  [ClientEvents.MEAL_PLAN_SIDES_PAIRING_EXPLORE_MORE_CLICK]: Record<string, never>;

  // Description: New property for "format" to indicate flexible_sides vs. mains_plus_sides
  [ClientEvents.MEAL_PLANNING_MVP_INTAKE_FORM_STEP]: {
    step: string;
    format: string;
  };

  [ClientEvents.MEAL_PLAN_FOR_USER_VALIDATION_FAILURE]: {
    newZipCode?: string;
    newReusablePackaging?: boolean;
    newPauseEndDate?: string;
    mealPlanId?: string;
    oldZipCode?: string;
    oldReusablePackaging?: boolean;
    oldPauseEndDate?: string;
  };

  [ClientEvents.TASTE_PROFILE_STORE_VALIDATION_FAILURE]: {
    oldMenuDietaryRestrictions?: string[];
    oldReplacementDietaryRestrictions?: string[];
    oldDietType?: string;
    oldNumAdults?: number;
    oldNumLittleEaters?: number;
    oldNumBigEaters?: number;
    oldPreferredProteins?: string[];
    oldCuisinePreferences?: string[];
    oldUserId?: string;
    newMenuDietaryRestrictions?: string[];
    newReplacementDietaryRestrictions?: string[];
    newDietType?: string;
    newNumAdults?: number;
    newNumLittleEaters?: number;
    newNumBigEaters?: number;
    newPreferredProteins?: string[];
    newCuisinePreferences?: string[];
    newUserId?: string;
  };

  // Description: any click to get to register screen on become a shef
  [ClientEvents.BECOME_A_SHEF_CLICK_GET_STARTED]: {
    section: string;
    phone?: string;
  };

  // Description: When user clicks the purchase button for a subscription. Before errors are shown
  [ClientEvents.SUBSCRIPTION_PURCHASE_ATTEMPT]: {
    cuisinePreferences: Record<string, boolean>;
    dietaryRestrictions: Record<string, boolean>;
    selectedSubscriptionPriceId: string;
    foodItemOrderData: {
      foodItemId: number;
      deliveryDay: string;
      quantity: number;
    }[];
    promoCode: string;
    promoCodeIsPercentage: boolean;
    promoCodeValue: number;
    promoCodeDescription: string;
    paymentMethod: string;
    subtotal: number;
    mondayDelivery: boolean;
    tuesdayDelivery: boolean;
    wednesdayDelivery: boolean;
    thursdayDelivery: boolean;
    fridayDelivery: boolean;
    saturdayDelivery: boolean;
    sundayDelivery: boolean;
  };

  // Description: When user clicks the purchase button for a subscription. Before errors are shown
  [ClientEvents.SUBSCRIPTION_MEAL_DETAILS_CLICK]: {
    foodItemId: number;
    currentPath: string;
  };

  // Description: When user purchases an item
  [ClientEvents.SUBSCRIPTION_PURCHASE]: {
    cuisinePreferences: Record<string, boolean>;
    dietaryRestrictions: Record<string, boolean>;
    selectedSubscriptionPriceId: string;
    foodItemOrderData: {
      foodItemId: number;
      deliveryDay: string;
      quantity: number;
    }[];
    promoCode: string;
    promoCodeIsPercentage: boolean;
    promoCodeValue: number;
    promoCodeDescription: string;
    paymentMethod: string;
    subtotal: number;
    subscriptionId: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_FOOD_ITEM_RENDERED]: {
    shefId?: string;
    shefIds?: string[];
    foodItemIds: number[];
    path: FoodItemRenderPath;
    foodItemType: FoodItemRenderType;
    location: MealPlanAnalyticsLocation;
    matchType: 'perfect_match' | 'imperfect_match';
    collectionId?: string;
    format: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_CAROUSEL_TAB]: {
    shefId: string;
    foodItemType: FoodItemRenderType;
  };

  [ClientEvents.MANAGE_SUBSCRIPTION_DROPDOWN]: {
    action: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_IMPERFECT_MATCH_RESULTS]: {
    numShefsAvailable: number;
  };
  [ClientEvents.MEAL_PLANNING_MVP_MORE_SHEFS_CLICK]: {
    numClicks: number;
  };
  [ClientEvents.MEAL_PLANNING_MVP_DISH_UPDATE_ATTEMPT]: {
    format: string;
  };

  [ClientEvents.MEAL_PLAN_OPEN_CART]: {
    page: string;
    step: string;
    cta_state: string;
    analyticsLocation: string;
    format: string;
  };

  [ClientEvents.MEAL_PLAN_CLEAR_CART]: {
    page: string;
    step: string;
    analyticsLocation: string;
    numMains: number;
    numMainServings: number;
    numSides: number;
    numAddOns: number;
  };

  [ClientEvents.MEAL_PLAN_NOT_AVAILABLE_ERROR]: {
    zipCode: string;
    mealPlansEnabled: boolean;
    isInMealPlanRegion: boolean;
    userId: string;
  };

  // Description: When a user removes any (Main, Side, Staple) dish from their MP cart
  [ClientEvents.MEAL_PLANNING_MVP_REMOVE_FROM_CART]: {
    shefId: string;
    foodItemId: string;
    shefPosition?: number;
    foodItemPosition: number;
    path: string;
    location: MealPlanAnalyticsLocation;
    format: string;
    collectionId?: string;
    /** Main servings in the cart after this change. */
    numSelectedServings?: number;
    /** Main servings the plan requires: `mealsPerWeek * numServings`. 0 before the cart store is populated. */
    requiredServings?: number;
    /** Raw, never folded into a display group, so the readout can split formats. */
    mealPlanFoodType: MealPlanFoodType;
  };

  // Description: When a user adds a Side or Staple dish to their MP cart
  [ClientEvents.MEAL_PLANNING_MVP_SIDE_OR_STAPLE_ADD]: {
    foodItemId: string;
    path?: string;
    location: MealPlanAnalyticsLocation;
    collectionId?: string;
    format: string;
  };

  // Description: When a user adds an Add-on dish to their MP cart
  [ClientEvents.MEAL_PLANNING_MVP_ADD_ON_ADD]: {
    foodItemId: string;
    path?: string;
    location: MealPlanAnalyticsLocation;
    collectionId?: string;
    format: string;
  };

  // Description: When a user adds a *Main* dish to their MP cart
  [ClientEvents.MEAL_PLANNING_MVP_ADD_TO_CART]: {
    shefId: string;
    foodItemId: string;
    shefPosition?: number;
    foodItemPosition: number;
    path: string;
    foodItemDescription?: string;
    foodItemName?: string;
    foodItemImageUrl?: string;
    shefName?: string;
    location: MealPlanAnalyticsLocation;
    collectionId?: string;
    format: string;
    /** True only when the item being added is a flex-servings main serving. */
    flexServing?: boolean;
    /** Main servings in the cart after this change. */
    numSelectedServings?: number;
    /** Main servings the plan requires: `mealsPerWeek * numServings`. 0 before the cart store is populated. */
    requiredServings?: number;
    /** Raw, never folded into a display group, so the readout can split formats. */
    mealPlanFoodType: MealPlanFoodType;
    /** Cents one serving adds on top of the plan price, from `getWSSPricePerServing`. A main's base is the plan price itself, so it reports its tier. */
    foodItemPrice: number;
  };

  [ClientEvents.MEAL_PLANNING_MVP_CHECKOUT_STARTED]: {
    weeklySubtotal: number;
    discounts: number;
    addOnsTotal: number;
    taxes: number;
    fees: number;
    finalPrice: number;
    nativePayAvailable?: string;
    format: string;
    /** Whether the user is on reusable (stainless steel) packaging when checkout starts. */
    isReusable: boolean;
  };

  [ClientEvents.MEAL_PLANNING_MVP_CHECKOUT_VALIDATION_FAILURE]: {
    hasPaymentSource: boolean;
    hasPhoneNumber: boolean;
    hasFullAddress: boolean;
    hasCustomerName: boolean;
    hasTimeWindow: boolean;
    paymentMethodSelected: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_CHECKOUT_VALIDATION_FAILURE_TREATMENT]: {
    customerName?: string;
    phoneNumber?: string;
    placeSearch?: string;
    timeWindow?: string;
    paymentMethod?: string;
    isValid?: boolean;
  };

  [ClientEvents.MEAL_PLANNING_MVP_DELIVERY_ADDRESS_SELECT]: {};

  [ClientEvents.MEAL_PLANNING_MVP_DELIVERY_TIME_SELECT]: {
    format: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_CHECKOUT_INPUT_STARTED]: {
    input: 'delivery-details' | 'gift-card' | 'promo-code' | 'native-pay' | 'cc-manual-input';
  };

  [ClientEvents.MEAL_PLANNING_MVP_PURCHASE]: {
    groupOrderId?: string;
    weeklySubtotal: number;
    discounts: number;
    addOnsTotal: number;
    taxes: number;
    fees: number;
    finalPrice: number;
    userType: MealPlanUserType;
    promoCode?: string;
    promoCodeType?: string;
    promoCodeDescription?: string;
    paymentMethodSelected?: string;
    nativePayAvailable?: string;
    giftCard?: string;
    paymentPath?: 'express' | 'native-pay';
    regionId?: number;
    mealPlanTypeId?: number;
    mealsPerWeek?: number;
    numServings?: number;
    isReusablePackaging?: boolean;
    mainServings?: number;
    flexServings?: number;
    uniqueMains?: number;
  };

  [ClientEvents.MEAL_PLANNING_MVP_PURCHASE_ATTEMPT]: {
    weeklySubtotal: number;
    discounts: number;
    addOnsTotal: number;
    taxes: number;
    fees: number;
    finalPrice: number;
    userType: MealPlanUserType;
    promoCode?: string;
    promoCodeType?: string;
    promoCodeDescription?: string;
    paymentMethodSelected?: string;
    nativePayAvailable?: string;
    giftCard?: string;
  };

  [ClientEvents.MEAL_PLANNING_MVP_SKIP_DELIVERY_ATTEMPT]: {
    deliveryDate?: string;
    deliveryNumber?: number;
    success?: boolean;
    userId?: string;
    format: string;
  };
  [ClientEvents.EXPRESS_CHECKOUT_ATTEMPT]: {
    walletType: string;
    surface: 'checkout_top' | 'native_pay_radio';
    version: 'v2';
    finalPrice: number;
  };
  [ClientEvents.EXPRESS_CHECKOUT_V1_WALLET_AVAILABILITY]: {
    applePay: boolean;
    googlePay: boolean;
    link: boolean;
    paypal: boolean;
    amazonPay: boolean;
  };
  [ClientEvents.EXPRESS_CHECKOUT_V1_FAILURE]: {
    walletType: string;
    surface?: 'checkout_top' | 'native_pay_radio';
    stage: 'submit' | 'confirm' | 'on-confirm';
    errorMessage: string;
    errorCode?: string;
    errorType?: string;
    declineCode?: string;
    setupIntentId?: string;
  };
  // Proxy for Link-button-clicked: fired when the Stripe PaymentElement reports
  // the user has selected Link as the payment method (proxy because we can't
  // observe Stripe's hosted Link modal lifecycle from outside the iframe).
  [ClientEvents.INLINE_LINK_BUTTON_CLICKED]: {
    surface: 'inline_payment';
  };
  /**
   * Proxy for Link-card-confirmed: fired after confirmSetup succeeds with Link
   * selected. cardLast4/cardBrand are sourced from the saveCreditCardFromPaymentMethod
   * response (the saved PM), not the Stripe Element, so they're absent if that save
   * did not return them. verificationMethod lives inside Stripe's hosted Link iframe
   * and is not capturable client-side.
   */
  [ClientEvents.INLINE_LINK_CARD_CONFIRMED]: {
    surface: 'inline_payment';
    cardType: 'saved' | 'new';
    cardLast4?: string;
    cardBrand?: string;
  };
  /**
   * Link flow abandoned: the user selected Link in the inline Element but unmounted
   * the entry (collapsed it or navigated away) before a successful confirm. Only the
   * 'card_entry' stage is observable from our surface — login / payment_methods /
   * verification happen inside Stripe's hosted iframe and cannot be tracked here.
   */
  [ClientEvents.INLINE_LINK_FLOW_EXITED]: {
    surface: 'inline_payment';
    stage: 'login' | 'payment_methods' | 'card_entry' | 'verification';
    reason?: string;
  };
  [ClientEvents.MEAL_PLANNING_UPCOMING_ORDER_RENDERED]: {
    isUpcoming: boolean;
    upcomingOrderPageStatus: string;
    deliveryDate?: string;
    deliveryNumber?: number;
    userId?: string;
    format: string;
  };
  [ClientEvents.MEAL_PLANNING_UNSKIP_DELIVERY]: {
    userId: string;
    deliveryDate?: string;
    deliveryNumber?: number;
    format: string;
    groupOrderId?: string;
    mealPlanId?: string;
  };
  [ClientEvents.MEAL_PLANNING_UNSKIP_DELIVERY_ERROR]: {
    userId: string;
    deliveryDate?: string;
    deliveryNumber?: number;
    format: string;
  };
  [ClientEvents.MEAL_PLANNING_UPCOMING_ORDER_BTN_CLICK]: {
    action: string;
    format?: string;
    deliveryDate?: string;
    deliveryNumber?: number;
  };
  [ClientEvents.MEAL_PLANNING_EDIT_DELIVERY_DATE_BTN_CLICK]: {
    action: string;
  };
  [ClientEvents.MEAL_PLANNING_UPCOMING_ORDER_PAGE_VIEW]: {
    status: string;
    format: string;
  };
  [ClientEvents.MEAL_PLANNING_PREFERENCES_SUBMITTED]: {
    adults?: number;
    bigEaters?: number;
    deliveryDate: string;
    deliveryDayOfWeek: string | null;
    deliveryTimeWindowStart?: number;
    deliveryTimeWindowEnd?: number;
    deliveryWindowId?: number;
    dietType?: string;
    intakeCompletedAtDate?: string;
    littleEaters?: number;
    mealsPerWeek?: number;
    menuDietaryRestrictions?: string[];
    moreInfo?: string;
    replacementDietaryRestrictions?: string[];
    reusablePackaging?: boolean;
    proteinPreferences?: string[];
    servingsPerMeal?: number;
    ingredientPriorities?: string[];
    topCuisines?: string[] | null;
    householdPreferences?: string[] | null;
  };
  [ClientEvents.MEAL_PLANNING_MANAGE_MEAL_PLAN_BTN_CLICK]: {
    action: string;
  };
  [ClientEvents.MEAL_PLAN_MODIFIED_CLICKED]: {
    action: string;
    numWeeks?: number;
    enabled?: boolean;
  };
  [ClientEvents.MEAL_PLANNING_MODIFY_PLAN_FAILURE]: {
    action: string;
  };
  [ClientEvents.MEAL_PLANNING_PLAN_SETTINGS_INPUT_CLICK]: {
    action: string;
  };
  [ClientEvents.MEAL_PLANNING_DELIVERY_DAY_INPUT_CLICK]: {
    action: string;
  };
  [ClientEvents.BACK_TO_TOP_BUTTON_CLICKED]: {
    scrollY: number;
  };
  [ClientEvents.MEAL_PLAN_MERCH_BAR_CLICK]: {
    tag: string;
  };
  [ClientEvents.MEAL_PLAN_SEARCH_RESULTS_DISPLAYED]: {
    query: string;
    numResults: number;
    numShefs: number;
    foodItemIds: number[];
    foodItemType: 'mains' | 'sides';
    path: FoodItemRenderPath;
  };
  [ClientEvents.MEAL_PLAN_SEARCH_RESULT_CLICK]: {
    query: string;
    foodItemId: number;
    foodItemName: string | null;
    shefId: string;
    foodItemType: 'mains' | 'sides';
  };
  [ClientEvents.MEAL_PLAN_SEARCH_ICON_CLICK]: Record<string, never>;
  [ClientEvents.PICK_UP_WHERE_YOU_LEFT_MODAL_ACTION]: {
    action: 'browse' | 'edit-preferences' | 'backdrop-click' | 'close-button-click' | 'escape-key';
  };
  [ClientEvents.DISH_SELECTION_CART_VALIDATION]: {
    removedFoodItemIds: number[];
    validatedFoodItemIds: number[];
    validationType: 'newSession' | 'newIntake' | 'editOrder' | 'checkoutTimer' | 'regionChanged';
  };
  [ClientEvents.NEW_USER_INTAKE_CHANGES]: {
    action: 'mealPlanTypeChanged' | 'deliveryDateChanged';
    cartFoodItemIds: number[];
    newValue: string;
    originalValue: string;
  };
  [ClientEvents.WSS_EXPLORE_TAB_CLICK]: {
    tab: 'weekly-shef-service' | 'one-time-ordering';
    userHasActiveMealPlan: boolean;
  };
  [ClientEvents.WSS_TAB_VIEW_UPCOMING]: {
    location: 'cta' | 'dish-card';
  };
  [ClientEvents.WSS_GET_STARTED_CLICK]: {
    location: MealPlanAnalyticsLocation;
  };
  [ClientEvents.WSS_ORGANIC_INFO_MODAL_SHOWN]: Record<string, never>;
  [ClientEvents.WSS_ORGANIC_INFO_MODAL_DISMISSED]: Record<string, never>;
  [ClientEvents.WSS_FLEX_SERVINGS_MINIMUM_MET_TOAST_SHOWN]: { numServings: number; pricePerServingCents: number };
  [ClientEvents.EXPLORE_PERSONAL_MEAL_PLAN_BANNER_SHOWN]: {
    hasMealPlan: boolean;
    regionId: number;
    currentUrl: string;
  };
  [ClientEvents.EXPLORE_PERSONAL_MEAL_PLAN_BANNER_CLICKED]: {
    hasMealPlan: boolean;
    regionId: number;
    currentUrl: string;
  };
  [ClientEvents.MEAL_PLAN_SHEF_BIO_CLICK]: {
    shefId: string;
    placement: ShefBioPlacements;
  };
  [ClientEvents.INTAKE_HOUSEHOLD]: {
    numAdults?: number;
    numBigEaters?: number;
    numChildren?: number;
  };
  [ClientEvents.INTAKE_PORTION_SIZE_SELECTED]: {
    portionSelection: string;
    mealPlanTypeId: number;
    numServings?: number;
    numMeals?: number;
  };
  [ClientEvents.INTAKE_SERVINGS]: {
    defaultNumServings?: number;
    selectedNumServings?: number;
  };
  [ClientEvents.INTAKE_SETTINGS]: {
    defaultNumServings?: number;
    selectedNumServings?: number;
    defaultNumMeals?: number;
    selectedNumMeals?: number;
  };
  [ClientEvents.INTAKE_DIET]: {
    dietType?: string;
    proteinPreferences?: string[];
  };
  [ClientEvents.INTAKE_PROTEIN]: {
    preferredProteins: string[] | null;
  };
  [ClientEvents.INTAKE_CUISINES]: {
    topCuisines: string[] | null;
  };
  [ClientEvents.INTAKE_HOUSEHOLD_PREFERENCES]: {
    householdPreferences: string[] | null;
  };
  [ClientEvents.INTAKE_DELIVERY_DAY]: {
    dayOfWeek?: string | null;
    datePosition?: number;
  };
  [ClientEvents.INTAKE_ORGANIC_AND_MORE]: {
    ingredientPriorities: string[];
  };
  [ClientEvents.INTAKE_INGREDIENTS_TO_AVOID]: {
    flag: string[];
    hide: string[];
  };
  /**
   * Diagnostic. Fires when a step renders a selection that disagrees with the persisted one —
   * the shape of a hydration bug, where the next save then overwrites real answers with nothing.
   */
  [ClientEvents.INTAKE_SELECTION_DESYNC]: {
    stepId: string;
    field: string;
    renderedCount: number;
    persistedCount: number;
    rendered: string[];
    persisted: string[];
  };
  [ClientEvents.INTAKE_INGREDIENTS_TO_AVOID_OTHER]: {
    text: string;
  };
  [ClientEvents.MEAL_PLAN_FILTERS_OPENED]: {
    cuisinePreferences: string[];
    dietaryFilters: string[];
    dietaryRestrictions: string[];
    dietType: string;
    proteins: string[];
    location: MealPlanAnalyticsLocation;
  };
  [ClientEvents.MEAL_PLAN_FILTERS_APPLIED]: {
    proteins: string[];
    dietType: string;
    dietaryFilters: string[];
    dietaryRestrictions: string[];
    cuisinePreferences?: string[];
  };
  [ClientEvents.MEAL_PLAN_CATEGORY_SORT_APPLIED]: {
    sortOption: 'recommended' | 'topRated' | 'newest';
    collectionId: string;
    numResults: number;
  };
  [ClientEvents.MEAL_PLAN_EDIT_TASTE_PROFILE_CLICKED]: {
    entryPoint: string;
  };
  [ClientEvents.MEAL_PLAN_TASTE_PROFILE_EDITED]: {
    dietType: string;
    preferredProteins: string[];
    dietaryFilters: string[];
    dietaryRestrictions: string[];
    cuisinePreferences: string[];
  };
  [ClientEvents.MEAL_PLAN_BROWSE_EXPERIENCE_CLICK]: {
    browseExperience: 'all-shefs' | 'favorites' | 'for-you' | 'your-shefs';
    variant?: string;
  };
  [ClientEvents.MEAL_PLAN_DISH_SELECTION_BACK_CLICK]: {
    location:
      | 'meal_plan_finalize'
      | 'meal_plan_checkout_meals'
      | 'meal_plan_checkout_sides'
      | 'meal_plan_checkout_add_ons'
      | 'meal_plan_checkout_review'
      | 'meal_plan_checkout_add_ons'
      | 'meal_plan_edit_meals'
      | 'meal_plan_edit_sides'
      | 'meal_plan_edit_review'
      | 'meal_plan_edit_add_ons';
    hasCartChanges?: boolean;
  };
  [ClientEvents.MEAL_PLAN_SIDES_CATEGORY_SELECTED]: {
    categoryId: string;
    categoryName: string;
    collectionId: string;
    categoryPosition: number;
    componentType: 'pill' | 'icon';
    selectionSource: 'carousel' | 'inline-link';
  };
  [ClientEvents.HOMEPAGE_FAQ_CLICK]: {
    type: 'faq' | 'chat';
    question?: string;
  };
  [ClientEvents.HOMEPAGE_MEAL_PLAN_CLICK]: {
    placement: string;
    type: 'landing-page' | 'homepage';
    hasZip: boolean;
    lpVariant?: string;
  };
  [ClientEvents.HOMEPAGE_MARKETPLACE_CLICK]: {
    placement: string;
  };
  [ClientEvents.HOMEPAGE_CATEGORY_CAROUSEL_CLICK]: {
    category: string;
  };
  [ClientEvents.HOMEPAGE_DISH_STORY_VIEW]: {
    category: string;
    dishTitle: string;
    dishIndex: number;
    trigger: 'auto' | 'tap' | 'rail';
  };
  [ClientEvents.HOMEPAGE_VIEW]: {
    type: 'landing-page' | 'homepage';
    lpVariant?: string;
  };
  [ClientEvents.HOMEPAGE_REDIRECT]: {
    intakeCompletedAtDate?: string;
    redirectUrl: string;
    originalUrl: string;
    visitedExplorePage: boolean;
    visitedMealPlans: boolean;
    mealPlanHasBeenRedirected: boolean;
    isInMealPlanRegion: boolean;
    zipCode?: string | null;
  };
  [ClientEvents.MEAL_PLAN_TASTE_PROFILE_INTAKE]: {
    dietType: string;
    preferredProteins: string[];
    dietaryFilters: string[];
    dietaryRestrictions: string[];
  };
  [ClientEvents.MEAL_PLAN_REVIEW_ORDER_SUMMARY_CLICK]: {
    type: 'open' | 'close';
    component: 'orderSummary' | 'priceBreakdown';
  };
  [ClientEvents.MEAL_PLAN_ORDER_DETAILS_CLICK]: {
    type: 'open' | 'close';
    component: 'orderSummary' | 'priceBreakdown';
  };
  [ClientEvents.CHECKOUT_TIMER_ELAPSED]: Record<string, never>;
  [ClientEvents.PAYMENT_DETAILS_INPUT_STARTED]: Record<string, never>;

  // Description: first change to the become a shef form
  [ClientEvents.BECOME_A_SHEF_ENTER_INFO]: Record<string, never>;

  [ClientEvents.BECOME_A_SHEF_CLICK_SIGN_UP]: Record<string, never>;

  [ClientEvents.REGISTER_SUCCESS]: {
    method?: AuthMethod;
    promoCode?: string;
  };
  [ClientEvents.LOGIN_MODAL_OPENED]: {
    signUpEntryPoint?: string | null;
    isClosable?: boolean;
    gateType?: LoginGateType;
  };
  [ClientEvents.LOGIN_MODAL_EMAIL_LOGIN_PAGE]: {
    signUpEntryPoint?: string | null;
    gateType?: LoginGateType;
  };
  [ClientEvents.LOGIN_MODAL_SIGNUP_PAGE]: {
    signUpEntryPoint?: string | null;
    method: AuthMethod;
    gateType?: LoginGateType;
  };
  [ClientEvents.LOGIN_MODAL_DISMISSED]: {
    signUpEntryPoint?: string | null;
    element: LoginModalDismissElements;
    gateType?: LoginGateType;
  };
  [ClientEvents.LOGIN_MODAL_EMAIL_ENTERED]: {
    signUpEntryPoint?: string | null;
    gateType?: LoginGateType;
  };
  [ClientEvents.LOGIN_PAGE_VIEWED]: {
    entryPath: 'login' | 'register';
    redirectPath: string;
    signUpEntryPoint: string;
  };
  [ClientEvents.SSO_SIGNUP_CLICKED]: {
    sso_name: string;
  };

  // Description: "How it Works" button
  [ClientEvents.NEW_USER_EDUCATION_VIEW]: Record<string, never>;
  [ClientEvents.NEW_USER_EDUCATION_STEP]: {
    step: string;
    videoProgress?: number;
  };

  [ClientEvents.CONSUMER_TRIAL_EXPLANATION_VIEW]: Record<string, never>;
  [ClientEvents.CONSUMER_TRIAL_ONBOARDING_VIEW]: {
    location: 'explore' | 'profile' | 'menu';
  };
  [ClientEvents.CONSUMER_TRIAL_ONBOARDING_ADVANCE]: {
    page: number;
  };
  [ClientEvents.CONSUMER_TRIAL_ONBOARDING_ACCEPT]: Record<string, never>;
  [ClientEvents.CONSUMER_HEADER_PROMO_BANNER_CLICK]: Record<string, never>;

  // Description: User response to CocktailIdReminderModal
  [ClientEvents.CONSUMER_HEADER_PERSISTENT_PROMO_BANNER_DISMISSED]: {
    promoCode: string;
    promoCodeIsPercentage: boolean;
    value: number;
    promoCodeDescription: string;
  };

  // Description: User response to CocktailIdReminderModal
  /** @deprecated 2022-03-11 */
  [ClientEvents.COCKTAIL_ID_REMINDER_ACTION]: {
    action: 'accept' | 'decline';
  };

  // Description: User attempt to submit cocktails order
  /** @deprecated 2022-03-11 */
  [ClientEvents.COCKTAIL_PURCHASE_ATTEMPT]: {
    foodOrderId: number;
    cocktailOrder: {
      shefId: string;
      subtotal: number;
      lineItems: {
        foodItemId: number;
        quantity: number;
      }[];
    }[];
    totalQuantity: number;
    totalSubtotal: number;
  };

  [ClientEvents.SHEF_APPLICATION_TRACKER_SHOW]: {
    shefFlows: ('dtt' | 'non-dtt' | 'shef-lite-market')[];
    numberStepsComplete: number;
    numberStepsTotal: number;
  };

  [ClientEvents.SHEF_APPLICATION_TRACKER_HIDE]: {};

  [ClientEvents.SHEF_APPLICATION_TRACKER_CLICK_STEP]: {
    shefFlows: ('dtt' | 'non-dtt' | 'shef-lite-market')[];
    numberStepsComplete: number;
    numberStepsTotal: number;
    stepName: string;
  };

  [ClientEvents.ATTEMPTED_REDIRECT_TO_CHROME_IOS]: {};

  // Description: Button clicked to add item to cart on an upsell menu
  [ClientEvents.UPSELL_CLICK_ADD_TO_CART]: {
    foodItemId: number;
    price: number;
  };

  // Description: Added step upsell closed after adding item to cart
  [ClientEvents.UPSELL_CLICK_CONTINUE]: {};
  // Description: Added step upsell closed with no items added to cart
  [ClientEvents.UPSELL_CLICK_NO_THANKS]: {};

  [ClientEvents.ADD_TO_CALENDAR_CLICK]: { type: string };

  [ClientEvents.FOLLOW_SHEF_START]: {
    location: FollowButtonClickLocation | MealPlanAnalyticsLocation;
    shefId: string;
  };
  [ClientEvents.FOLLOW_SHEF_STOP]: {
    location: FollowButtonClickLocation | MealPlanAnalyticsLocation;
    shefId: string;
  };
  [ClientEvents.FOOD_ITEM_FAVORITE_ADDED]: {
    location: MealPlanAnalyticsLocation;
    foodItemId: number;
    dishName?: string;
    shefId?: string;
    cuisine?: string | null;
  };
  [ClientEvents.FOOD_ITEM_FAVORITE_REMOVED]: {
    location: MealPlanAnalyticsLocation;
    foodItemId: number;
    dishName?: string;
    shefId?: string;
    cuisine?: string | null;
  };
  [ClientEvents.SHEF_FAVORITE_ADDED]: {
    surface: FollowButtonClickLocation | MealPlanAnalyticsLocation;
    shefId: string;
    shefName: string;
  };
  [ClientEvents.SHEF_FAVORITE_REMOVED]: {
    surface: FollowButtonClickLocation | MealPlanAnalyticsLocation;
    shefId: string;
    shefName: string;
  };
  [ClientEvents.FAVORITES_INFO_MODAL_SHOWN]: {};
  [ClientEvents.FAVORITES_INFO_MODAL_DISMISSED]: {};
  [ClientEvents.SHEF_POST_VIEW]: {
    shefId: string;
    postId: string;
    location: 'email' | 'feed' | 'menu';
  };
  [ClientEvents.VIEW_MENU_REVIEWS_SECTION]: {};
  [ClientEvents.CLICK_REVIEWS_BUTTON]: {
    position: 'bio' | 'section';
  };

  // Description: When user hovers over the food safety certified icon on desktop and clicks on mobile
  [ClientEvents.VIEW_FOOD_SAFETY_POPOVER]: {
    date: string;
    user_id: string;
    shefId: string;
  };

  [ClientEvents.REVIEW_FOOD_ITEM_IMAGE_CLICK]: {
    foodItemSlug: string;
    shefId: string;
    orderRating?: number;
    ratingPercent: number | null | undefined;
  };

  // Description: When the app is going to attempt to request for an app review. This is triggered before validating
  // whether we will actually make the native request.
  [ClientEvents.APP_REVIEW_REQUEST_ATTEMPT]: {
    lastRequestedAtMs: number | undefined;
  };

  // Description: When the request for an app review is skipped and the native request isn't made.
  [ClientEvents.APP_REVIEW_REQUEST_SKIPPED]: {
    reason: 'unavailable' | 'too soon';
    lastRequestedAtMs: number | undefined;
  };

  // Description: When the app has attempted to show the native app review prompt. There's no guarantee that the prompt
  // actually shows. The number of times it can show is limited by Google and Apple.
  [ClientEvents.APP_REVIEW_REQUESTED]: {
    lastRequestedAtMs: number | undefined;
    requestedAtMs: number;
  };

  // Description: When the app has attempted to show the native app review prompt, but an exception was thrown.
  [ClientEvents.APP_REVIEW_REQUEST_FAILED]: {
    lastRequestedAtMs: number | undefined;
    requestedAtMs: number;
  };

  [ClientEvents.WEDGE_PAGE_VIEW]: {
    slug: string;
  };

  [ClientEvents.APP_REDIRECT]: {
    redirectUrl: string;
    utmSource?: string;
    utmMedium: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
  };

  // Description: Push notification opened, bringing user to the app.
  [ClientEvents.PUSH_NOTIFICATION_OPEN]: {
    // If this notification was sent from customer io, it'll have tracking data.
    trackData?: {
      deliveryId: string;
      deliveryToken: string;
    };
  };

  [ClientEvents.PUSH_NOTIFICATION_PROMPT_SHOW]: {
    numDismissed: number;
  };

  [ClientEvents.PUSH_NOTIFICATION_PROMPT_HIDE]: {
    numDismissed: number;
  };

  // Description: When user either grants or denies the push notification permission request
  [ClientEvents.PUSH_NOTIFICATION_PERMISSION_REQUESTED]: {
    status: string;
  };

  // Description: user is navigated to the app listing page
  [ClientEvents.NAVIGATE_TO_APP_LISTING]: Record<string, never>;

  // Description: user dismissed app upsell banner
  [ClientEvents.APP_UPSELL_BANNER_DISMISSED]: Record<string, never>;

  [ClientEvents.DELIVERY_DATE_TOOLTIP_OPEN]: {
    tooltipPage: 'menu' | 'explore';
  };

  [ClientEvents.CHAT_WIDGET_OPEN]: {
    consumerId: string;
    shefId: string;
  };

  [ClientEvents.FLAGGED_CHAT_MODAL_EVENT]: {
    role: string;
    event: string;
    keywordCategory: string | null;
    text: string;
    senderId: string;
  };

  [ClientEvents.SHEF_ROSTER_SELECTOR_CLICK]: {
    shefId: string;
    rosterPosition: number;
    rosterStatus: 'FAVORITE' | 'RECOMMENDED' | 'NONE';
    hasItemInCart: boolean;
  };
  [ClientEvents.SHEF_ROSTER_LOADED]: {
    roster: {
      shefId: string;
      carouselPosition: number;
      hasItemInCart: boolean;
      rosterStatus: 'FAVORITE' | 'RECOMMENDED' | 'NONE';
    }[];
  };

  [ClientEvents.SHEF_MENU_CLICK]: {
    shefId: string;
    location?: MealPlanAnalyticsLocation;
  };

  [ClientEvents.SHEF_PROFILE_LOAD_MORE_REVIEWS]: {
    shefId: string;
  };

  [ClientEvents.TRACKING_PERMISSION_REQUESTED]: {
    status: string;
  };

  [ClientEvents.SINGLE_CART_STARTED_CHECKOUT]: {
    numberOfOrders: number;
  };

  [ClientEvents.MULTI_CART_STARTED_CHECKOUT]: {
    numberOfOrders: number;
    totalCartSubtotal: number;
  };

  [ClientEvents.MULTI_CART_ADD_ORDER_BTN_CLICKED]: {};

  [ClientEvents.MULTI_CART_STARTED_NEW_ORDER]: {
    numberOfOrders: number;
    shefId: string;
  };

  /**
   * Fires when adding a dish would move the whole cart to a different delivery date, i.e. when the
   * "Change your delivery date?" modal is shown. `isNewDateDeliverableToZip` is false when the date
   * the dish would move the cart to is not one the zip can receive delivery on — that combination
   * strands the cart on a date the picker cannot offer back, so it is the number to watch rather
   * than the raw impression count.
   */
  [ClientEvents.MULTI_CART_DELIVERY_DATE_CONFLICT_SHOWN]: {
    cartDeliveryDate: string;
    newDeliveryDate: string;
    isNewDateDeliverableToZip: boolean;
    numItemsUnavailableOnNewDate: number;
    shefId: string;
    foodItemId: number;
  };

  [ClientEvents.MULTI_CART_DELIVERY_DATE_CONFLICT_RESOLVED]: {
    resolution: 'accepted' | 'cancelled';
    cartDeliveryDate: string;
    newDeliveryDate: string;
    isNewDateDeliverableToZip: boolean;
    numItemsRemoved: number;
  };

  [ClientEvents.DISH_FIRST_CART_CHANGE_DELIVERY_DATE]: {
    reason: 'started-new-order' | 'manual-change' | 'stale';
    previousDeliveryDate: string;
    newDeliveryDate: string;
  };

  [ClientEvents.DISH_FIRST_CLICK_NEXT_DAY_BANNER]: {
    msBeforeMidnight: number;
    nextDayOfWeek: string;
  };

  [ClientEvents.DISH_FIRST_LAZY_LOAD_QUICK_ADD_ITEM]: {
    result: 'success' | 'error';
    userRetryCount: number;
    waitTimeMs: number;
  };

  /**
   * Description: When shef clicks on any of the See Your Reviews buttons on the My Ratings tab
   * categoryName is the order tag
   */
  [ClientEvents.SHEF_DASHBOARD_SEE_YOUR_REVIEWS_BTN_CLICK]: {
    categoryName: string;
  };

  /**
   * Description: When shef clicks on any of the copy link buttons on the Promote Yourself tab
   * linkTo is what the link directs to (i.e. shef menu)
   */
  [ClientEvents.SHEF_DASHBOARD_PROMOTE_YOURSELF_COPY_LINK_CLICK]: {
    linkTo?: string;
  };

  /**
   * Description: When shef clicks the feedback link in the beta notice on the Insights tab
   */
  [ClientEvents.SHEF_INSIGHTS_FEEDBACK_CLICK]: Record<string, never>;

  /**
   * Description: When shef answers "Is this report helpful?" in the header of the Insights tab
   * isHelpful is true for thumbs up and false for thumbs down
   */
  [ClientEvents.SHEF_INSIGHTS_REPORT_HELPFUL_VOTE]: {
    isHelpful: boolean;
  };

  // Description: When user chooses which source to pull the location (zip) from.
  [ClientEvents.LOCATION_SOURCE_SELECTED]: {
    type: AppLocationSource;
  };
  // Description: When user requests the location permission.
  [ClientEvents.LOCATION_PERMISSION_REQUESTED]: {
    status: string;
  };
  // Description: When user sets the location to use.
  [ClientEvents.LOCATION_SET]: {
    type: AppLocationSource;
  };

  [ClientEvents.CLICK_HELP_WIDGET]: Record<string, never>;

  [ClientEvents.CELEBRITY_EVENT_ERROR]: {
    errors: string[];
  };

  [ClientEvents.REVIEW_GROUP_ORDER_BUTTON_CLICK]: {
    groupOrderId: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_SHOW]: {
    groupOrderId: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_STEP]: {
    groupOrderId: string;
    step: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_PARTIAL_SAVE]: {
    groupOrderId: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_SUBMIT]: {
    groupOrderId: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_SUBMIT_SUCCESS]: {
    groupOrderId: string;
  };

  [ClientEvents.GROUP_ORDER_RATING_MODAL_SUBMIT_FAILURE]: {
    groupOrderId: string;
    errors: string[];
  };

  [ClientEvents.RATE_RECENT_GROUP_ORDER_MODAL_SHOW]: {
    groupOrderId: string;
  };

  [ClientEvents.RATE_RECENT_GROUP_ORDER_MODAL_CLICK]: {
    groupOrderId: string;
    ratingPercent: number | null;
  };

  // Unsaved Changes Modal shown
  [ClientEvents.UNSAVED_CHANGES_MODAL_SHOWN]: {
    location: MealPlanAnalyticsLocation;
  };

  // Unsaved Changes Modal action
  [ClientEvents.UNSAVED_CHANGES_MODAL_ACTION]: {
    action: 'discard-and-exit' | 'keep-shopping';
    location: MealPlanAnalyticsLocation;
  };

  /*
   * in October 2023 we moved browserTrackerId (deviceId) and session_started events to be generated by the shef-web server
   * during the initial request to our servers. In doing this, we may be generating session_started events with a new deviceId
   * for browsers that arent new. In order to fix this, the client checks its local storage for an existing browserTrackerId
   * and if it finds one, sends a reconciliation event, which we use in ETL to fix up the session_started events.
   */
  [ClientEvents.BROWSER_TRACKER_RECONCILIATION]: {
    serverDeviceId: string; // The one set by server which is wrong
    clientDeviceId: string; // The actual one set client side
  };

  [ClientEvents.REFERRAL_REDIRECT]: {
    code: string;
    pattern: string;
    redirectPattern: string;
    redirectUrl: string;
    url: string;
    shefId: string | undefined;
    foodItemSlug: string | undefined;
  };

  [ClientEvents.WAITLIST_ACTION_CLICKED]: {
    type: string;
  };

  [ClientEvents.PRE_CHECKOUT_UPSELL_MODAL_VIEW]: {};

  [ClientEvents.PRE_CHECKOUT_UPSELL_MODAL_CLOSE]: {};

  [ClientEvents.PRE_CHECKOUT_UPSELL_REFRESH_SUGGESTIONS]: {
    page: number;
    hasExhaustedSuggestions: boolean;
  };

  [ClientEvents.PRE_CHECKOUT_UPSELL_ADD_ALL]: {
    // upsell metdata
    upsellFoodItemIds: number[];
    upsellShefIds: string[];
    upsellSubtotal: number;
    // cart metdata
    foodItemIdsInCart: number[];
    numFoodItemsInCart: number;
    shefIdsInCart: string[];
    cartSubtotal: number;
  };

  [ClientEvents.PRE_CHECKOUT_UPSELL_MODAL_VIEW]: {};

  [ClientEvents.PRE_CHECKOUT_UPSELL_MODAL_CLOSE]: {};

  [ClientEvents.PRE_CHECKOUT_UPSELL_REFRESH_SUGGESTIONS]: {
    page: number;
    hasExhaustedSuggestions: boolean;
  };

  [ClientEvents.PRE_CHECKOUT_UPSELL_ADD_ALL]: {
    // upsell metdata
    upsellFoodItemIds: number[];
    upsellShefIds: string[];
    upsellSubtotal: number;
    // cart metdata
    foodItemIdsInCart: number[];
    numFoodItemsInCart: number;
    shefIdsInCart: string[];
    cartSubtotal: number;
  };

  [ClientEvents.FOOD_ITEM_PRICE_RECOMMENDATION]: {
    foodItemId: number;
    shefId: string;
    recommendedPrice: number;
    inPriceRange: boolean;
    price: number;
    servingSize: string;
    dishName: string;
    foodType: string;
    priceChanged: boolean;
    originalPrice?: number;
    newPrice: number;
  };
  [ClientEvents.FOOD_ITEM_VALIDATION_AUTO_FIX]: {
    autoFixState: 'offered' | 'accepted';
    ruleId: string;
    ruleName: string;
    foodItemId: number;
    foodItemField: string;
  };
  [ClientEvents.FOOD_ITEM_VALIDATION_UNAVAILABLE]: {
    foodItemId: number;
    unavailableReason: 'request_failed' | 'findings_not_persisted';
  };
  [ClientEvents.FOOD_ITEM_VALIDATION_DISH_SUBMITTED]: {
    foodItemId: number;
    dishEditFlow: string;
    isNewDish: boolean;
    isFoodItemValidationEnabled: boolean;
    findingsShownCount: number;
    findingsDismissedCount: number;
    autoFixesAcceptedCount: number;
    ruleIdsShown: string[];
  };
  [ClientEvents.SHEF_DASHBOARD_ADD_NEW_DISH_BTN_CLICK]: {
    isFoodItemValidationEnabled: boolean;
  };
  [ClientEvents.SHEF_MENU_NAV_CLICK]: {
    shefId: string;
    section: string;
  };
  [ClientEvents.SHEF_PRICE_OUT_OF_RANGE_MODAL]: {
    skipped: boolean;
  };
  [ClientEvents.SHEF_WSS_MERCH_MODAL_VIEW]: {
    feature: 'core-menu-overview' | 'limited-time' | 'seasonal' | 'weekly-rotation';
  };
  [ClientEvents.SHEF_FRINGE_MODAL_EXPLANATION_VIEW]: {};
  [ClientEvents.SHEF_FRINGE_MODAL_MORE_VIEWED]: {};
  [ClientEvents.SHEF_FRINGE_MODAL_DISMISSED]: {};
  [ClientEvents.SHEF_FRINGE_MODAL_ACCEPTED]: {};
  [ClientEvents.SUBSCRIPTION_CANCEL_MODAL_CONFIRMATION]: {
    mealPlanGroupOrderId?: string | null;
    subscriptionId?: string | null;
    deliveryDateToCancel?: string | null;
  };
  [ClientEvents.SHEF_MESSAGE_CLICK]: {
    recipient: string | null;
  };
  [ClientEvents.CANCEL_REASONS_SUBMITTED]: Record<string, never>;
  [ClientEvents.CANCEL_FREE_FORM_STEP_SUBMITTED]: Record<string, never>;
  [ClientEvents.SUBSCRIPTION_CANCELLATION_REASON_CAPTURED]: {
    selectedReasons: Array<{
      primaryReason: string;
      secondaryReason: string;
    }>;
    feedback: string;
    otherText: string;
  };
  [ClientEvents.CANCEL_REASONS_ACTION_CLICK]: {
    action: 'contact-support' | 'change-address' | 'change-dow' | 'change-window' | 'change-payment' | 'pause';
  };
  [ClientEvents.MEAL_PLAN_CANCELLATION_WARNING_SHOWN]: {
    hasCleaningFeeExemption: boolean;
    hasLegacyPricing: boolean;
    mealPlanId: string;
  };
  [ClientEvents.MEAL_PLAN_CANCELLATION_WARNING_DISMISSED]: {
    mealPlanId: string;
  };
  [ClientEvents.MEAL_PLAN_CANCELLATION_WARNING_BYPASSED]: {
    mealPlanId: string;
  };
  [ClientEvents.MEAL_SELECTION_FOOTER_NEXT_CLICK]: {
    nextButtonLabel: string;
    nextButtonDisabled?: boolean;
    location?: string;
    mainServings?: number;
    flexServings?: number;
  };
  [ClientEvents.MEAL_PLAN_CHECKOUT_NEXT_STEP]: {
    currentStepUrl: string;
    currentStepIndex: number;
    nextStepUrl: string | undefined;
    dynamicStepsCount: number;
    mealPlanAddOnsActive: boolean;
    numSelectedMains: number;
    mealsPerWeek: number;
    mainServings?: number;
    flexServings?: number;
  };
  [ClientEvents.MEAL_PLAN_CHECKOUT_PREV_STEP]: {
    currentStepUrl: string;
    currentStepIndex: number;
  };
  [ClientEvents.MEAL_PLAN_CHECKOUT_STEP_ERROR]: {
    error: string;
    step?: string;
    dynamicStepUrls?: string[];
    currentIndex?: number;
    dynamicStepsCount?: number;
  };
  [ClientEvents.MEAL_PLAN_INTAKE_NEXT_STEP]: {
    stepIndex: number;
    stepName: string | undefined;
    stepUrl: string | undefined;
    isValid: boolean | undefined;
    totalSteps: number;
    isLastStep: boolean;
  };
  [ClientEvents.MEAL_PLAN_INTAKE_STEP_ERROR]: {
    stepIndex: number;
    stepsLength: number;
    stepUrls: string[];
  };
  [ClientEvents.MEAL_PLAN_INTAKE_COMPLETE_PREFERENCE]: Record<string, never>;
  [ClientEvents.MEAL_PLAN_INTAKE_REGION_INVALIDATION]: {
    regionId: number;
    zipCode?: string;
    invalidations: string[];
    redirectedToStep?: string;
  };
  [ClientEvents.MEAL_PLAN_INTAKE_REDIRECT_TO_CHECKOUT]: Record<string, never>;
  [ClientEvents.MEAL_PLAN_INTAKE_LAST_STEP]: Record<string, never>;
  [ClientEvents.STEPPER_NEXT_STEP_CLICKED]: {
    currentStepIndex: number;
    stepName: string;
    stepUrl: string;
    isValid: boolean | undefined;
    totalSteps: number;
  };
  [ClientEvents.STEPPER_STEP_ERROR]: {
    stepQueryParam: string;
    availableStepUrls: string[];
  };
  [ClientEvents.STEPPER_NAVIGATION_BLOCKED]: {
    shouldProceed: boolean | undefined;
    nextStepIdx: number;
    nextStepName: string | undefined;
    currentStepIndex: number;
    stepName: string;
  };
  [ClientEvents.STEPPER_NEXT_CALLBACK_ERROR]: {
    currentStepIndex: number;
    stepName: string;
    errorMessage: string;
  };
  [ClientEvents.SELF_REPORTED_ATTRIBUTION]: {
    attributionSource: string;
    other?: string;
    surface?: 'embedded' | 'modal';
  };
  [ClientEvents.SELF_REPORTED_ATTRIBUTION_SHOWN]: {
    surface?: 'embedded' | 'modal';
  };

  [ClientEvents.CATEGORY_CAROUSEL_CLICK]: {
    collectionId: string;
  };

  [ClientEvents.CATEGORY_CAROUSEL_CLEAR]: {
    collectionId: string;
    path: 'clear' | 'deselect' | 'newSelection';
  };

  [ClientEvents.COLLECTION_RENDERED]: {
    collectionId: string;
    foodItemIds: number[];
    foodItemType: FoodItemRenderType;
    path: FoodItemRenderPath;
  };

  [ClientEvents.LEAD_CAPTURE_FIELD_SUBMITTED]: {
    step: LeadCaptureStep;
    success: boolean;
  };
  [ClientEvents.LEAD_CAPTURE_ERROR]: {
    step: LeadCaptureStep;
    reason: 'invalid_email' | 'invalid_phone' | 'missing_terms';
  };
  [ClientEvents.LEAD_CAPTURE_MODAL_DISMISSED]: {
    step: LeadCaptureStep;
  };
  [ClientEvents.LEAD_CAPTURE_MODAL_VIEWED]: {
    step: LeadCaptureStep;
    trigger: 'time' | 'scroll' | 'exit_intent';
    device: 'mobile' | 'desktop';
  };
  [ClientEvents.LEAD_CAPTURE_AUTOFILL_APPLIED]: Record<string, never>;
  [ClientEvents.PACKAGING_TOGGLE_CHANGED]: {
    selection: 'standard' | 'stainless steel';
    location: 'cart' | 'checkout';
  };
  [ClientEvents.REUSABLE_PACKAGING_OPT_IN_MODAL_SHOWN]: ReusablePackagingOptInModalContext;
  [ClientEvents.REUSABLE_PACKAGING_OPT_IN_MODAL_HOW_IT_WORKS_CLICK]: ReusablePackagingOptInModalContext;
  [ClientEvents.REUSABLE_PACKAGING_OPT_IN_MODAL_OPT_IN_CLICK]: ReusablePackagingOptInModalContext;
  [ClientEvents.REUSABLE_PACKAGING_OPT_IN_MODAL_DISMISSED]: Record<string, never>;

  // Description: Every page load on a 3P landing page under shef.com/lp/<slug>/. LP context (lp_slug, campaign, agency, UTMs) is attached by shef-tag.js via its enhance() step, so the payload itself is empty.
  [ClientEvents.LP_PAGE_VIEW]: Record<string, never>;
  // Description: Fired once per session (6h window) on a 3P landing page.
  [ClientEvents.LP_SESSION_STARTED]: {
    startedAt: number;
    sessionId: string;
    referrer: string;
    language: string;
  };
  // Description: Visitor submitted the zip form on a landing page.
  [ClientEvents.LP_ZIP_SUBMIT]: {
    destination: 'meal-plan-intake' | 'explore-one-time';
    zip: string;
  };
  // Description: Visitor clicked a CTA on a landing page. `target` names what was clicked; `position` names where on the page.
  [ClientEvents.LP_CTA_CLICK]: {
    target: string;
    position?: string;
  };

  /**
   * Fires once per page load rather than once per render of the button, so that a shef who opens
   * the editor and backs out is counted a single time. Treat it as the funnel's denominator.
   */
  [ClientEvents.TABLERUN_SUGGESTED_PRICE_SHOWN]: {
    storefrontId: string;
    suggestedCents: number;
  };
  [ClientEvents.TABLERUN_SUGGESTED_PRICE_TAPPED]: {
    storefrontId: string;
    suggestedCents: number;
  };
  [ClientEvents.TABLERUN_ENABLE_SUBSCRIPTIONS]: {
    storefrontId: string;
    suggestedCents: number;
    savedCents: number;
    edited: boolean;
  };
  [ClientEvents.TABLERUN_DISABLE_SUBSCRIPTIONS]: {
    storefrontId: string;
    previousCents: number;
  };
}
